/**
 * @krisspy-file
 * @type page
 * @name "NewBookingFlow"
 * @title "New Booking Flow"
 * @description "Multi-step booking wizard with availability checking and payment"
 * @routes ["/bookings/new"]
 * @pathExample "/bookings/new"
 * @layout "DashboardLayout"
 * @flowName "booking-management"
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  Calendar,
  Clock,
  Package,
  Plus,
  Truck,
  User,
  CreditCard,
  ChevronLeft,
  Search,

  ChevronRight,
  Check,
  AlertTriangle,
  Loader2,
  Mail,
  Phone,
  FileText,
  X,
  Zap,
  ArrowRight,
  Ticket,
  CheckCircle,
  XCircle,
  Globe
} from 'lucide-react';
import { getLocations } from '../services/locations.service';
import { checkAvailability, createBooking, searchCustomers, type AvailableProduct, type CustomerResult } from '../services/bookings.service';
import { getProducts } from '../services/products.service';
import { isActiveProduct, isHiddenProduct } from '../utils/productVisibility';
import HiddenProductBadge from '../components/HiddenProductBadge';
import { getCategories } from '../services/categories.service';
import { getMerchantById, type MerchantWithCurrency } from '../services/merchants.service';
import { calculatePrice, pricingRuleColumns, type ItemPricingRuleColumns } from '../services/pricing.service';
import {
  getAddressAutocomplete,
  validateAddress,
  calculateDeliveryPrice,
  type AddressSuggestion,
  type ValidatedAddress,
} from '../services/delivery.service';
import { validateDiscountCode, incrementDiscountCodeUsage } from '../services/discountCodes.service';
import { insertAuditLog } from '../services/bookingAuditLog.service';
import { recordManualPayment } from '../services/paymentTransactions.service';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import type { Database } from '../lib/database.types';
import { formatCurrency, getCurrencySymbol } from '../lib/currency';
import { computeBookingTotals } from '../lib/bookingTotals';
import { zonedWallTimeToUtc, asZoned, zonedViewToUtc } from '../lib/timezone';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { mergeCustomFieldTranslations } from '../lib/translations';
import { customFieldPlaceholder, sanitizeCustomFieldHtml, type CustomFieldConfig } from '../lib/customFields';
import { stripHtml } from '../utils/htmlConversion';
import { getLocationTranslations } from '../services/locations.service';
import NumberInput from '../components/NumberInput';
import { BOOKING_WEBHOOK_URL, determineSendNow } from '../lib/bookingWebhook';



type Location = Database['public']['Tables']['locations']['Row'];

/**
 * i18n keys for the temporal rejection reasons returned by the shared
 * check_discount_temporal_validity DB function (see discountCodes.service).
 */
const DISCOUNT_REJECTION_REASON_KEYS: Record<string, string> = {
  not_found: 'discountCodes.reasonNotFound',
  missing_rental_start: 'discountCodes.reasonMissingRentalStart',
  not_yet_valid: 'discountCodes.reasonNotYetValid',
  expired: 'discountCodes.reasonExpired',
  invalid_day: 'discountCodes.reasonInvalidDay',
  outside_time_window: 'discountCodes.reasonOutsideTimeWindow'
};

// Define rentable product types (bikes, scooters, etc.) - excludes addons
// This matches the user frontend pattern for separating products from addons
const RENTABLE_PRODUCT_TYPES = ['bike', 'sup', 'kayak', 'other'];

// Define booking flow steps (Delivery step is conditionally shown)
const BASE_STEPS = [
  { id: 1, name: 'Location', icon: MapPin },
  { id: 2, name: 'Date & Time', icon: Calendar },
  { id: 3, name: 'Products', icon: Package },
  { id: 4, name: 'Add-ons', icon: Plus },
  { id: 5, name: 'Delivery', icon: Truck, requiresDelivery: true },
  { id: 6, name: 'Customer', icon: User },
  { id: 7, name: 'Payment', icon: CreditCard },
];

interface BundleComponentResolved {
  product_type_id: string;
  product_name: string;
  quantity: number;
  size: string | null;
  inventoryItemIds: string[];  // assigned item IDs for individual inventory
  inventoryType: 'individual' | 'pool' | 'unlimited';
}

interface SelectedProduct {
  product_type_id: string;
  productName: string;
  size: string | null;
  quantity: number;
  pricePerUnit: number;
  inventoryItemId: string | null;
  inventoryType: 'individual' | 'pool' | 'unlimited';
  /** The rule pricePerUnit came from; a bundle's components get the bundle's. */
  ruleColumns: ItemPricingRuleColumns;
  isBundle?: boolean;
  bundleComponents?: BundleComponentResolved[];
}

export default function NewBookingFlow() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, profile, merchantId, locationIds, isPlatformAdmin, accessControl } = useAuth();
  const { currentLanguage, defaultLanguage, getLanguageConfig } = useLanguage();
  const { formatDateTime, formatDate, timezone } = useDateFormatter();
  const [currentStep, setCurrentStep] = useState(1);
  // Starts true so the location step shows a spinner (not an empty grid) until locations load
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Merchant data (for currency)
  const [merchant, setMerchant] = useState<MerchantWithCurrency | null>(null);
  const currencyCode = merchant?.currency_code || 'USD';
  const currencySymbol = getCurrencySymbol(currencyCode);

  // Step 1: Location
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  // Step 2: Date & Time
  const [useNow, setUseNow] = useState(false);
  // UI-only: keep active step visible in horizontally scrollable stepper
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepItemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // UI-only: allow mobile "cart" bar to scroll user to order summary
  const orderSummaryRef = useRef<HTMLDivElement | null>(null);

  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [durationDays, setDurationDays] = useState(1);
  const [durationHours, setDurationHours] = useState(0);

  // Step 3: Products (availability results)
  const [availableProducts, setAvailableProducts] = useState<AvailableProduct[]>([]);
  const [productTypeDetails, setProductTypeDetails] = useState<Map<string, { product_type: string; pool_quantity: number | null; inventory_type: string | null; active: boolean | null; hidden: boolean | null }>>(new Map());
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // Step 3: Product search & category filtering
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [categoryChips, setCategoryChips] = useState<{ id: string; category_name: string }[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // Step 4: Add-ons
  const [selectedAddons, setSelectedAddons] = useState<SelectedProduct[]>([]);


  const cartCount = selectedProducts.length + selectedAddons.length;

  // Step 5: Delivery (if delivery location)
  const [deliveryAddressInput, setDeliveryAddressInput] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [validatedAddress, setValidatedAddress] = useState<ValidatedAddress | null>(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryPrice, setDeliveryPrice] = useState<number | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [loadingDeliveryPrice, setLoadingDeliveryPrice] = useState(false);

  // Step 6: Customer
  const [customerFirstName, setCustomerFirstName] = useState('');
  const [customerLastName, setCustomerLastName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerLanguage, setCustomerLanguage] = useState('');
  const [customerMarketingConsent, setCustomerMarketingConsent] = useState(false);

  // Customer search (lookup from previous bookings)
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState<CustomerResult[]>([]);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [loadingCustomerSearch, setLoadingCustomerSearch] = useState(false);
  const [customerAutofilled, setCustomerAutofilled] = useState(false);
  const customerSearchRef = useRef<HTMLDivElement>(null);

  // Step 7: Payment
  const [paymentMethod, setPaymentMethod] = useState<'paid' | 'link' | 'later'>('later');
  const [internalNotes, setInternalNotes] = useState('');

  // Custom fields
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, string>>({});
  const [resolvedCustomFields, setResolvedCustomFields] = useState<CustomFieldConfig[]>([]);

  // Discount code
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState<{
    valid: boolean;
    discount: number;
    error: string | null;
    reason?: string;
    discountCodeId?: string;
    discountType?: string;
  } | null>(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  // Load locations on mount
  useEffect(() => {
    loadLocations();
    loadMerchant();
  }, []);

  // UI-only: when step changes, ensure active step is scrolled into view on small screens
  useEffect(() => {
    const node = stepItemRefs.current[currentStep];
    if (!node) return;

    try {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    } catch {
      // ignore
    }
  }, [currentStep]);

  // Resolve custom_fields when location or language changes
  useEffect(() => {
    if (!selectedLocation) {
      setResolvedCustomFields([]);
      setCustomFieldValues({});
      return;
    }

    const loc = selectedLocation as any;
    const baseCustomFields: CustomFieldConfig[] = Array.isArray(loc.custom_fields) ? loc.custom_fields : [];

    // Reset custom field values when location changes
    setCustomFieldValues({});

    // If admin language differs from default, fetch location translations
    if (currentLanguage && currentLanguage !== defaultLanguage) {
      getLocationTranslations(selectedLocation.id).then(({ data: translationsData }) => {
        const translation = translationsData?.find((t: any) => t.language_code === currentLanguage);
        if (translation) {
          const translatedCustomFields = (translation as any).custom_fields;
          setResolvedCustomFields(
            mergeCustomFieldTranslations(baseCustomFields, Array.isArray(translatedCustomFields) ? translatedCustomFields : null)
          );
        } else {
          setResolvedCustomFields(baseCustomFields);
        }
      });
    } else {
      setResolvedCustomFields(baseCustomFields);
    }
  }, [selectedLocation?.id, currentLanguage, defaultLanguage]);

  async function loadLocations() {
    setLoading(true);
    const { data, error: fetchError } = await getLocations(
      { merchantId, locationIds, isPlatformAdmin },
      { active: true }
    );

    if (fetchError) {
      setError(t('newBooking.errorLoadLocations'));
      setLoading(false);
      return;
    }

    if (data) {
      setLocations(data);
      // Only one location to choose from: pick it and start on Date & Time.
      // The Location step stays in the stepper as completed.
      if (data.length === 1) {
        setSelectedLocation(data[0]);
        setCurrentStep(2);
      }
    }
    setLoading(false);
  }

  async function loadMerchant() {
    if (!merchantId) return;

    const { data, error: fetchError } = await getMerchantById(merchantId);

    if (fetchError) {
      console.warn('Failed to load merchant data:', fetchError);
      // Don't set error state - just use defaults
      return;
    }

    if (data) {
      setMerchant(data);
      if (!customerLanguage) {
        const supported = Array.isArray(data.supported_languages) ? data.supported_languages : [];
        const fallback = data.default_language || 'en';
        const preferred = currentLanguage && supported.includes(currentLanguage) ? currentLanguage : fallback;
        setCustomerLanguage(preferred);
      }
    }
  }

  // Customer search: debounced query against search_customers RPC
  useEffect(() => {
    if (customerSearchQuery.length < 2 || !merchantId) {
      setCustomerSearchResults([]);
      setCustomerSearchOpen(false);
      return;
    }

    const timeout = setTimeout(async () => {
      setLoadingCustomerSearch(true);
      const { data } = await searchCustomers(merchantId, customerSearchQuery, 10);
      setCustomerSearchResults(data || []);
      setCustomerSearchOpen(true);
      setLoadingCustomerSearch(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [customerSearchQuery, merchantId]);

  // Close customer search dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (customerSearchRef.current && !customerSearchRef.current.contains(e.target as Node)) {
        setCustomerSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load categories when a location with category_step_enabled is selected
  useEffect(() => {
    if (!selectedLocation) {
      setCategoryChips([]);
      setSelectedCategoryIds([]);
      return;
    }

    if (selectedLocation.category_step_enabled === false) {
      setCategoryChips([]);
      setSelectedCategoryIds([]);
      return;
    }

    async function loadCategories() {
      const { data: cats } = await getCategories(accessControl, { active: true });
      if (cats) {
        setCategoryChips(cats.map((c: any) => ({ id: c.id, category_name: c.category_name })));
      }
    }
    loadCategories();
  }, [selectedLocation]);

  function selectCustomer(c: CustomerResult) {
    setCustomerFirstName(c.customer_firstname);
    setCustomerLastName(c.customer_lastname);
    setCustomerEmail(c.customer_email);
    setCustomerPhone(c.customer_phone);
    if (c.language) setCustomerLanguage(c.language);
    setCustomerSearchOpen(false);
    setCustomerSearchQuery('');
    setCustomerAutofilled(true);
  }

  // Calculate end date/time based on start and duration.
  // All wall-clock interpretation happens in the merchant's timezone, so
  // results are correct even when the admin's browser is in a different TZ.
  const calculateEndDateTimeDetailed = (): { end: string | null; adjusted: boolean } => {
    // Determine start date/time
    let start: Date;
    if (useNow) {
      start = new Date();
    } else {
      if (!startDate || !startTime) return { end: null, adjusted: false };
      start = zonedWallTimeToUtc(startDate, startTime, timezone);
      if (isNaN(start.getTime())) return { end: null, adjusted: false };
    }

    // Check if this is calendar days (0 hours) or exact hours
    if (durationHours === 0 && durationDays > 0) {
      // Calendar days mode: return at closing time on the last day.
      // If that day is closed or its closing is at/before start (e.g. booking
      // created after the shop's closing time), advance day-by-day until we
      // land on an open day whose close is strictly after start. Cap the loop
      // to avoid spinning on a fully-closed location.
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const openingHours = (selectedLocation?.opening_hours as Record<string, { open: string; close: string }> | null) || {};
      const zonedDay = asZoned(start, timezone);
      zonedDay.setDate(zonedDay.getDate() + durationDays - 1);

      for (let i = 0; i < 7; i++) {
        const dayOfWeek = dayNames[zonedDay.getDay()];
        const candidate = openingHours[dayOfWeek]?.close;
        if (candidate && /^\d{1,2}:\d{2}$/.test(candidate)) {
          const [h, m] = candidate.split(':').map((v) => parseInt(v, 10));
          const zonedClose = new Date(zonedDay.getTime());
          if ((h === 0 && m === 0) || (h === 24 && m === 0)) {
            // Treat "00:00"/"24:00" close as end-of-day: next day at 00:00
            zonedClose.setHours(0, 0, 0, 0);
            zonedClose.setDate(zonedClose.getDate() + 1);
          } else {
            zonedClose.setHours(h, m, 0, 0);
          }
          const endUtc = zonedViewToUtc(zonedClose, timezone);
          if (!isNaN(endUtc.getTime()) && endUtc.getTime() > start.getTime()) {
            return { end: endUtc.toISOString(), adjusted: i > 0 };
          }
        }
        zonedDay.setDate(zonedDay.getDate() + 1);
      }
      // Exhausted: no open day with a valid closing time in the next week.
      return { end: null, adjusted: false };
    } else {
      // Exact hours mode: add total hours to start time (pure instant math)
      const durationMs = (durationDays * 24 + durationHours) * 60 * 60 * 1000;
      const end = new Date(start.getTime() + durationMs);
      return { end: end.toISOString(), adjusted: false };
    }
  };

  const calculateEndDateTime = (): string | null => calculateEndDateTimeDetailed().end;

  // Calculate total duration in hours
  const calculateTotalDurationHours = () => {
    return durationDays * 24 + durationHours;
  };

  // Actual wall-clock elapsed hours between start and computed end. In
  // calendar-day mode (durationHours === 0) the end is the location's
  // closing time, so this can be much less than durationDays * 24 — and
  // that smaller number is what the hours-rule pricing branch should see.
  const calculateElapsedHours = (): number | undefined => {
    const endIso = calculateEndDateTime();
    if (!endIso) return undefined;
    const start = useNow ? new Date() : zonedWallTimeToUtc(startDate, startTime, timezone);
    if (isNaN(start.getTime())) return undefined;
    const ms = new Date(endIso).getTime() - start.getTime();
    if (!isFinite(ms) || ms < 0) return undefined;
    return ms / (1000 * 60 * 60);
  };

  // Format street address with number in correct order based on locale
  const formatStreetAddress = (address: ValidatedAddress | null): string | null => {
    if (!address) return null;

    const streetName = address.street_name;
    const streetNumber = address.street_number;
    const country = address.country;

    // If no street name, return null
    if (!streetName) return null;

    // If no street number, return just the street name
    if (!streetNumber) return streetName;

    // US/UK style: number before street (e.g., "123 Main Street")
    const usUkCountries = ['US', 'GB', 'UK', 'CA', 'AU', 'NZ', 'IE'];
    if (country && usUkCountries.includes(country.toUpperCase())) {
      return `${streetNumber} ${streetName}`;
    }

    // EU style: number after street (e.g., "Main Street 123")
    return `${streetName} ${streetNumber}`;
  };

  // Calculate total price (products + addons + delivery - discount)
  const calculateTotal = () => {
    const productsTotal = selectedProducts.reduce(
      (sum, p) => sum + p.pricePerUnit * p.quantity,
      0
    );
    const addonsTotal = selectedAddons.reduce(
      (sum, a) => sum + a.pricePerUnit * a.quantity,
      0
    );
    const deliveryTotal = deliveryPrice || 0;
    const discountAmount = discountValidation?.valid ? discountValidation.discount : 0;
    return Math.max(0, productsTotal + addonsTotal + deliveryTotal - discountAmount);
  };

  // A discount code that covers the whole booking leaves nothing to collect, so
  // the booking is paid regardless of which payment option was selected — a
  // payment link or "pay later" for 0 would leave it stuck as open.
  const isFullyDiscounted =
    !!discountValidation?.valid && discountValidation.discount > 0 && calculateTotal() <= 0;
  const effectivePaymentMethod: 'paid' | 'link' | 'later' = isFullyDiscounted ? 'paid' : paymentMethod;

  // Validate discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim() || !selectedLocation) {
      setDiscountValidation(null);
      return;
    }

    setValidatingDiscount(true);

    // Calculate order amount (without discount)
    const orderAmount = selectedProducts.reduce((sum, p) => sum + p.pricePerUnit * p.quantity, 0) +
                       selectedAddons.reduce((sum, a) => sum + a.pricePerUnit * a.quantity, 0) +
                       (deliveryPrice || 0);

    // Get product type IDs
    const productTypeIds = [
      ...selectedProducts.map(p => p.product_type_id),
      ...selectedAddons.map(a => a.product_type_id)
    ].filter(Boolean) as string[];

    // Rental start instant (UTC) — required for validity_basis = 'rental_start'
    // codes; the shared DB function checks the code's basis for us.
    const rentalStart = useNow
      ? new Date()
      : (startDate && startTime ? zonedWallTimeToUtc(startDate, startTime, timezone) : null);

    const result = await validateDiscountCode({
      code: discountCode,
      merchantId: selectedLocation.merchant_id,
      orderAmount,
      productTypeIds,
      rentalStart
    });

    setDiscountValidation(result);
    setValidatingDiscount(false);
  };

  // Clear discount code
  const handleClearDiscount = () => {
    setDiscountCode('');
    setDiscountValidation(null);
  };

  const formatDuration = () => {
    if (durationDays > 0 && durationHours > 0) {
      return `${t('newBooking.daysCount', { count: durationDays })} ${t('newBooking.hoursCount', { count: durationHours })}`;
    } else if (durationDays > 0) {
      return t('newBooking.daysCount', { count: durationDays });
    } else {
      return t('newBooking.hoursCount', { count: durationHours });
    }
  };

  // Render Order Summary (shown from step 3 onward)
  const renderOrderSummary = () => {
    if (currentStep < 3) return null;

    const parsedStart = startDate && startTime ? zonedWallTimeToUtc(startDate, startTime, timezone) : null;
    const startDateTime = useNow
      ? new Date()
      : (parsedStart && !isNaN(parsedStart.getTime()) ? parsedStart : null);

    const endDateTimeStr = calculateEndDateTime();
    const endDateTime = endDateTimeStr ? new Date(endDateTimeStr) : null;

    const total = calculateTotal();

    // Calculate tax based on location settings
    const taxRate = selectedLocation?.tax_rate || 0;
    const taxIncluded = selectedLocation?.tax_included ?? true;
    const taxName = selectedLocation?.tax_name || 'VAT';

    // Calculate tax amount
    const taxAmount = taxIncluded
      ? total * (taxRate / (1 + taxRate)) // Tax is already included in prices
      : total * taxRate; // Tax needs to be added on top

    return (
      <div ref={orderSummaryRef} className="space-y-md sticky top-4" data-testid="new-booking-order-summary">
        <div className="card p-md">
          <h3 className="font-bold text-base mb-sm text-primary">{t('newBooking.orderSummary')}</h3>

          <div className="space-y-1 text-sm text-secondary">
            {selectedLocation && (
              <div className="flex items-center gap-xs">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{selectedLocation.location_name}</span>
              </div>
            )}
            {startDateTime && (
              <div className="flex items-center gap-xs">
                <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{formatDateTime(startDateTime)}</span>
              </div>
            )}
            <div className="flex items-center gap-xs">
              <Clock className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{formatDuration()}</span>
            </div>
            {endDateTime && (
              <div className="flex items-center gap-xs">
                <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                <span>{t('newBooking.returnPrefix')} {formatDateTime(endDateTime)}</span>
              </div>
            )}
          </div>

          {/* Selected Products */}
          {(selectedProducts.length > 0 || selectedAddons.length > 0) && (
            <>
              <div className="border-t border-border pt-sm mt-sm">
                <div className="flex items-center justify-between mb-xs">
                  <div className="flex items-center gap-xs">
                    <Package className="w-3.5 h-3.5 text-secondary" />
                    <h4 className="font-semibold text-xs text-primary">{t('newBooking.items')}</h4>
                  </div>
                  <span className="text-xs text-secondary">
                    {t('newBooking.linesCount', { count: selectedProducts.length + selectedAddons.length })}
                  </span>
                </div>

                {/* Group products by name and size, sum quantities */}
                {(() => {
                  // Create a map of unique products
                  const productMap = new Map<string, {
                    productName: string;
                    size: string | null;
                    quantity: number;
                    pricePerUnit: number;
                    indices: number[];
                  }>();

                  selectedProducts.forEach((product, index) => {
                    const key = `${product.productName}-${product.size || 'null'}`;
                    const existing = productMap.get(key);

                    if (existing) {
                      existing.quantity += product.quantity;
                      existing.indices.push(index);
                    } else {
                      productMap.set(key, {
                        productName: product.productName,
                        size: product.size,
                        quantity: product.quantity,
                        pricePerUnit: product.pricePerUnit,
                        indices: [index]
                      });
                    }
                  });

                  return Array.from(productMap.values()).map((groupedProduct, idx) => {
                    // Check if this is a bundle product
                    const firstIdx = groupedProduct.indices[0];
                    const firstItem = selectedProducts[firstIdx];
                    const isBundle = firstItem?.isBundle;

                    return (
                    <div key={idx}>
                    <div className="flex items-center justify-between py-2 text-sm leading-relaxed">
                      <div className="flex items-center gap-xs flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => groupedProduct.indices.reverse().forEach(i => handleRemoveProduct(i))}
                          className="text-danger hover:text-danger/80"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="text-primary truncate leading-relaxed md:leading-loose">
                          {groupedProduct.productName}{groupedProduct.size ? ` (${groupedProduct.size})` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-xs">
                        <NumberInput
                          value={groupedProduct.quantity}
                          onChange={(newQty) => {
                            const clamped = Math.max(1, Math.floor(newQty || 1));
                            const firstProduct = selectedProducts[groupedProduct.indices[0]];
                            const td = productTypeDetails.get(firstProduct.product_type_id);
                            const invType = td?.inventory_type || firstProduct.inventoryType;

                            if (invType === 'individual') {
                              // For individual inventory, maintain separate entries with distinct inventoryItemIds
                              const currentQty = groupedProduct.quantity;
                              if (clamped > currentQty) {
                                // Add new entries from items not already consumed by any cart line
                                // (standalone OR bundle), preserving allocation order.
                                const remaining = cartAvailability.remainingIndividualItems(firstProduct.product_type_id, firstProduct.size);
                                const maxAvailable = currentQty + remaining.length;
                                const effectiveNew = Math.min(clamped, maxAvailable) - currentQty;
                                if (effectiveNew > 0) {
                                  const newEntries: SelectedProduct[] = [];
                                  for (let i = 0; i < effectiveNew; i++) {
                                    newEntries.push({
                                      product_type_id: firstProduct.product_type_id,
                                      productName: firstProduct.productName,
                                      size: firstProduct.size,
                                      quantity: 1,
                                      pricePerUnit: firstProduct.pricePerUnit,
                                      ruleColumns: firstProduct.ruleColumns,
                                      inventoryItemId: remaining[i].item_id,
                                      inventoryType: firstProduct.inventoryType,
                                    });
                                  }
                                  setSelectedProducts([...selectedProducts, ...newEntries]);
                                }
                                if (clamped > maxAvailable) {
                                  setError(`Only ${maxAvailable} ${firstProduct.productName}${firstProduct.size ? ` (${firstProduct.size})` : ''} available.`);
                                }
                              } else if (clamped < currentQty) {
                                // Remove entries from the end
                                const indicesToRemove = new Set(groupedProduct.indices.slice(clamped));
                                setSelectedProducts(selectedProducts.filter((_, i) => !indicesToRemove.has(i)));
                              }
                            } else {
                              // Pool/unlimited: collapse to single entry
                              handleUpdateQuantity(groupedProduct.indices[0], clamped);
                              groupedProduct.indices.slice(1).reverse().forEach(i => handleRemoveProduct(i));
                            }
                          }}
                          mode="int"
                          min={1}
                          className="input w-16 h-8 text-xs text-center px-1"
                          data-testid={`new-booking-order-summary-product-qty-${groupedProduct.productName}-${groupedProduct.size || 'one-size'}`}
                        />
                        <span
                          className="font-semibold text-primary w-20 text-right"
                          data-testid={`new-booking-order-summary-line-total-${groupedProduct.productName}-${groupedProduct.size || 'one-size'}`}
                        >
                          {formatCurrency(groupedProduct.pricePerUnit * groupedProduct.quantity, currencyCode)}
                        </span>
                      </div>
                    </div>
                    {/* Show bundle components indented below the bundle */}
                    {isBundle && firstItem.bundleComponents && firstItem.bundleComponents.map((comp, cIdx) => (
                      <div key={cIdx} className="flex items-center justify-between py-0.5 text-xs text-secondary pl-8">
                        <span className="truncate">
                          {comp.quantity > 1 ? `${comp.quantity}x ` : ''}{comp.product_name}{comp.size ? ` (${comp.size})` : ''}
                        </span>
                        <span className="text-secondary w-20 text-right">{formatCurrency(0, currencyCode)}</span>
                      </div>
                    ))}
                    </div>
                    );
                  });
                })()}

                {/* Group addons by name and size, sum quantities */}
                {(() => {
                  const addonMap = new Map<string, {
                    productName: string;
                    size: string | null;
                    quantity: number;
                    pricePerUnit: number;
                    indices: number[];
                  }>();

                  selectedAddons.forEach((addon, index) => {
                    const key = `${addon.productName}-${addon.size || 'null'}`;
                    const existing = addonMap.get(key);

                    if (existing) {
                      existing.quantity += addon.quantity;
                      existing.indices.push(index);
                    } else {
                      addonMap.set(key, {
                        productName: addon.productName,
                        size: addon.size,
                        quantity: addon.quantity,
                        pricePerUnit: addon.pricePerUnit,
                        indices: [index]
                      });
                    }
                  });

                  return Array.from(addonMap.values()).map((groupedAddon, idx) => (
                    <div key={`addon-${idx}`} className="flex items-center justify-between py-2 text-sm leading-relaxed">
                      <div className="flex items-center gap-xs flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => groupedAddon.indices.reverse().forEach(i => handleRemoveAddon(i))}
                          className="text-danger hover:text-danger/80"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        <span className="text-primary truncate leading-relaxed md:leading-loose">
                          {groupedAddon.productName}{groupedAddon.size ? ` (${groupedAddon.size})` : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-xs">
                        <NumberInput
                          value={groupedAddon.quantity}
                          onChange={(newQty) => {
                            const clamped = Math.max(1, Math.floor(newQty || 1));
                            const firstAddon = selectedAddons[groupedAddon.indices[0]];
                            const td = productTypeDetails.get(firstAddon.product_type_id);
                            const invType = td?.inventory_type || firstAddon.inventoryType;

                            if (invType === 'individual') {
                              // For individual inventory, maintain separate entries with distinct inventoryItemIds
                              const currentQty = groupedAddon.quantity;
                              if (clamped > currentQty) {
                                // Add new entries from items not already consumed by any cart line
                                // (standalone OR bundle), preserving allocation order.
                                const remaining = cartAvailability.remainingIndividualItems(firstAddon.product_type_id, firstAddon.size);
                                const maxAvailable = currentQty + remaining.length;
                                const effectiveNew = Math.min(clamped, maxAvailable) - currentQty;
                                if (effectiveNew > 0) {
                                  const newEntries: SelectedProduct[] = [];
                                  for (let i = 0; i < effectiveNew; i++) {
                                    newEntries.push({
                                      product_type_id: firstAddon.product_type_id,
                                      productName: firstAddon.productName,
                                      size: firstAddon.size,
                                      quantity: 1,
                                      pricePerUnit: firstAddon.pricePerUnit,
                                      ruleColumns: firstAddon.ruleColumns,
                                      inventoryItemId: remaining[i].item_id,
                                      inventoryType: firstAddon.inventoryType,
                                    });
                                  }
                                  setSelectedAddons([...selectedAddons, ...newEntries]);
                                }
                                if (clamped > maxAvailable) {
                                  setError(`Only ${maxAvailable} ${firstAddon.productName}${firstAddon.size ? ` (${firstAddon.size})` : ''} available.`);
                                }
                              } else if (clamped < currentQty) {
                                // Remove entries from the end
                                const indicesToRemove = new Set(groupedAddon.indices.slice(clamped));
                                setSelectedAddons(selectedAddons.filter((_, i) => !indicesToRemove.has(i)));
                              }
                            } else {
                              // Pool/unlimited: collapse to single entry
                              handleUpdateAddonQuantity(groupedAddon.indices[0], clamped);
                              groupedAddon.indices.slice(1).reverse().forEach(i => handleRemoveAddon(i));
                            }
                          }}
                          mode="int"
                          min={1}
                          className="input w-16 h-8 text-xs text-center px-1"
                          data-testid={`new-booking-order-summary-addon-qty-${groupedAddon.productName}-${groupedAddon.size || 'one-size'}`}
                        />
                        <span
                          className="font-semibold text-primary w-20 text-right"
                          data-testid={`new-booking-order-summary-line-total-${groupedAddon.productName}-${groupedAddon.size || 'one-size'}`}
                        >
                          {formatCurrency(groupedAddon.pricePerUnit * groupedAddon.quantity, currencyCode)}
                        </span>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* Delivery Price (if applicable) */}
              {deliveryPrice !== null && deliveryPrice > 0 && (
                <div className="border-t border-border pt-sm mt-sm">
                  <div className="flex justify-between items-center text-sm">
                    <div className="flex items-center gap-xs">
                      <MapPin className="w-3.5 h-3.5 text-secondary" />
                      <span className="text-primary">{t('newBooking.delivery')}</span>
                    </div>
                    <span className="font-semibold text-primary">{formatCurrency(deliveryPrice, currencyCode)}</span>
                  </div>
                  {deliveryDistance !== null && (
                    <p className="text-xs text-secondary text-right mt-0.5">
                      {deliveryDistance.toFixed(1)} km
                    </p>
                  )}
                </div>
              )}

              {/* Tax (when not included in prices) */}
              {taxRate > 0 && !taxIncluded && (
                <div className="border-t border-border pt-sm mt-sm">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-secondary">{taxName} ({(taxRate * 100).toFixed(0)}%)</span>
                    <span className="font-semibold text-primary">{formatCurrency(taxAmount, currencyCode)}</span>
                  </div>
                </div>
              )}

              {/* Total */}
              <div className="border-t border-border pt-sm mt-sm">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-primary">{t('newBooking.total')}</span>
                  <span className="text-sm font-bold text-primary" data-testid="new-booking-order-summary-total">
                    {formatCurrency(taxIncluded ? total : total + taxAmount, currencyCode)}
                  </span>
                </div>
                {taxRate > 0 && taxIncluded && (
                  <p className="text-xs text-secondary text-right mt-0.5">
                    {t('newBooking.inclTax', { taxName, pct: (taxRate * 100).toFixed(0), amount: formatCurrency(taxAmount, currencyCode) })}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Check availability (Step 3)
  const handleCheckAvailability = async () => {
    if (!selectedLocation) {
      setError('Please select location');
      return;
    }

    if (!useNow && (!startDate || !startTime)) {
      setError('Please select start date and time');
      return;
    }

    setLoadingAvailability(true);
    setError(null);
    setProductSearchQuery('');
    setSelectedCategoryIds([]);

    let start: string;
    let end: string;

    if (useNow) {
      // For "now" bookings, use current time
      const now = new Date();
      start = now.toISOString();

      const calculatedEnd = calculateEndDateTime();
      if (!calculatedEnd) {
        setError(t('newBooking.errorInvalidConfig'));
        setLoadingAvailability(false);
        return;
      }
      end = calculatedEnd;
    } else {
      // For scheduled bookings, use selected date/time interpreted in the
      // merchant's timezone (NOT the admin's browser timezone).
      const startDateTime = zonedWallTimeToUtc(startDate, startTime, timezone);
      if (isNaN(startDateTime.getTime())) {
        setError(t('newBooking.errorInvalidConfig'));
        setLoadingAvailability(false);
        return;
      }
      start = startDateTime.toISOString();

      const calculatedEnd = calculateEndDateTime();

      if (!calculatedEnd) {
        setError(t('newBooking.errorInvalidConfig'));
        setLoadingAvailability(false);
        return;
      }

      end = calculatedEnd;
    }

    console.log('🔐 Access control for getProducts:', { merchantId, locationId: null, isPlatformAdmin });
    console.log('📍 Selected location:', selectedLocation.id, selectedLocation.location_name);
    console.log('📍 Selected location full object:', selectedLocation);
    console.log('📍 Is delivery location?', selectedLocation.deliveries);

    // First, fetch availability - this RPC handles all complex logic:
    // - delivery_eligible flag
    // - available_location_ids (cross-location inventory)
    // - additional_product_type_ids (shared inventory items)
    const availabilityResult = await checkAvailability({
      locationId: selectedLocation.id,
      startDate: start,
      endDate: end,
      skipTimeGates: true, // Admin override: ignore pre-booking hours and cutoff restrictions
    });

    if (availabilityResult.error) {
      setError(availabilityResult.error);
      setLoadingAvailability(false);
      return;
    }

    console.log('✅ Availability check returned:', availabilityResult.data?.length, 'products');

    // Now fetch ALL product types for this merchant to build the enrichment map
    // We don't filter by location because the RPC already determined which products are available
    const productTypesResult = await getProducts(
      { merchantId, locationIds: null, isPlatformAdmin },
      {}, // No filters - get all product types for the merchant
      currentLanguage,
      merchant?.default_language || 'en'
    );

    console.log('📦 Product types result:', productTypesResult);
    console.log('📦 Product types error:', productTypesResult.error);
    console.log('📦 Product types data:', productTypesResult.data);
    console.log('📦 Product types count:', productTypesResult.data?.length);
    console.log('📦 Sample product type:', productTypesResult.data?.[0]);

    if (productTypesResult.error) {
      console.error('❌ Product types error:', productTypesResult.error);
      setError(productTypesResult.error);
      setLoadingAvailability(false);
      return;
    }

    if (availabilityResult.data && productTypesResult.data) {
      // Create a mapping from product_type_id to product details (type, pool_quantity, inventory_type)
      const productTypeMap = new Map<string, { product_type: string; pool_quantity: number | null; inventory_type: string | null; active: boolean | null; hidden: boolean | null }>(
        productTypesResult.data.map((pt: any) => [pt.id, {
          product_type: pt.product_type,
          pool_quantity: pt.pool_quantity,
          inventory_type: pt.inventory_type,
          active: pt.active,
          hidden: pt.hidden
        }])
      );

      // Translated names from product_type_translations (already merged by getProducts)
      const translatedNameMap = new Map<string, string>(
        productTypesResult.data.map((pt: any) => [pt.id, pt.product_name])
      );

      console.log('📦 Product type map size:', productTypeMap.size);
      console.log('📦 Product type map entries:', Array.from(productTypeMap.entries()).slice(0, 5));

      // Store product type details for validation
      setProductTypeDetails(productTypeMap);

      // Enrich availability results with product_type field and translated name
      // Preserve the product_type from RPC if it already exists and is valid
      const enrichedProducts = availabilityResult.data.map(product => ({
        ...product,
        product_name: translatedNameMap.get(product.product_type_id) || product.product_name,
        product_type: product.product_type && product.product_type !== 'unknown'
          ? product.product_type
          : (productTypeMap.get(product.product_type_id)?.product_type || product.product_type || 'unknown')
      }));

      console.log('✅ Availability check returned:', enrichedProducts.length, 'products');
      console.log('🔍 Sample enriched product:', enrichedProducts[0]);
      console.log('🔍 Product type:', enrichedProducts[0]?.product_type);
      console.log('🔍 Product type from RPC (before enrichment):', availabilityResult.data[0]?.product_type);
      console.log('🔍 Product type ID from availability:', availabilityResult.data[0]?.product_type_id);
      console.log('🔍 Can we find this product_type_id in map?', productTypeMap.has(availabilityResult.data[0]?.product_type_id));
      console.log('🔍 Map lookup result:', productTypeMap.get(availabilityResult.data[0]?.product_type_id));

      setAvailableProducts(enrichedProducts);
    } else {
      console.warn('⚠️  No availability data returned');
    }
    setLoadingAvailability(false);
  };

  // Navigate to next step
  const handleNext = async () => {
    // Validation for each step
    if (currentStep === 1 && !selectedLocation) {
      setError(t('newBooking.errorSelectLocation'));
      return;
    }

    if (currentStep === 2) {
      if (!useNow && (!startDate || !startTime)) {
        setError(t('newBooking.errorSelectDateTime'));
        return;
      }
      if (!useNow) {
        const parsed = zonedWallTimeToUtc(startDate, startTime, timezone);
        const year = asZoned(parsed, timezone).getFullYear();
        if (isNaN(parsed.getTime()) || year < 2000 || year > 2100) {
          setError(t('newBooking.errorInvalidDate'));
          return;
        }
      }
      if (durationDays === 0 && durationHours === 0) {
        setError(t('newBooking.errorSelectDuration'));
        return;
      }
      // Check availability when moving from step 2 to step 3
      setError(null);
      await handleCheckAvailability();
    }

    if (currentStep === 3 && selectedProducts.length === 0) {
      setError(t('newBooking.errorSelectProduct'));
      return;
    }

    if (currentStep === 5 && selectedLocation?.deliveries && !validatedAddress) {
      setError(t('newBooking.errorSelectAddress'));
      return;
    }

    if (currentStep === 6) {
      if (!customerFirstName || !customerLastName || !customerEmail || !customerPhone) {
        setError(t('newBooking.errorCustomerInfo'));
        return;
      }
      // Validate required custom fields
      const missingFields = resolvedCustomFields
        .filter(f => f.required && !(customFieldValues[f.key] || '').trim())
        .map(f => f.label);
      if (missingFields.length > 0) {
        setError(t('newBooking.errorRequiredCustomFields', { fields: missingFields.join(', ') }));
        return;
      }
    }

    setError(null);

    // Skip delivery step if location doesn't support deliveries
    if (currentStep === 4 && !selectedLocation?.deliveries) {
      setCurrentStep(6);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  // With a single location the Location step is skipped, so Date & Time is the first step
  const firstStep = locations.length === 1 ? 2 : 1;

  // Navigate to previous step
  const handleBack = () => {
    if (currentStep <= firstStep) return;
    // Skip delivery step if location doesn't support deliveries
    if (currentStep === 6 && !selectedLocation?.deliveries) {
      setCurrentStep(4);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  // ── Single source of truth for cart-aware availability ──
  //
  // The raw availability from check_availability_enhanced (`availableProducts`)
  // reflects only committed bookings, not the items sitting in this unsaved cart.
  // Computing "what's left" in each handler separately is what let bundles/pools
  // be over-added. Instead, derive consumption once (across standalone lines,
  // addons AND bundle components) and expose remaining-availability helpers that
  // every display, guard and assignment path reads from.
  type CartComponent = { product_type_id: string; quantity: number; size?: string | null };
  const cartAvailability = useMemo(() => {
    const consumedItemIds = new Set<string>();
    const consumedPoolByType = new Map<string, number>();
    const addPool = (typeId: string, qty: number) =>
      consumedPoolByType.set(typeId, (consumedPoolByType.get(typeId) || 0) + qty);

    for (const p of [...selectedProducts, ...selectedAddons]) {
      if (p.isBundle && p.bundleComponents) {
        for (const c of p.bundleComponents) {
          if (c.inventoryType === 'individual') c.inventoryItemIds.forEach(id => consumedItemIds.add(id));
          else if (c.inventoryType === 'pool') addPool(c.product_type_id, c.quantity);
        }
      } else if (p.inventoryType === 'individual') {
        if (p.inventoryItemId) consumedItemIds.add(p.inventoryItemId);
      } else if (p.inventoryType === 'pool') {
        addPool(p.product_type_id, p.quantity);
      }
    }

    // Pool capacity = booking-adjusted pool_quantity from the RPC (max across rows).
    const poolCapacity = (typeId: string): number => {
      const rows = availableProducts.filter(p => p.product_type_id === typeId && p.inventory_type === 'pool');
      return rows.length ? Math.max(0, ...rows.map(r => r.pool_quantity || 0)) : 0;
    };
    const remainingPool = (typeId: string): number =>
      Math.max(0, poolCapacity(typeId) - (consumedPoolByType.get(typeId) || 0));

    // Individual items for a type/size, optionally excluding cart-consumed ones.
    const individualItems = (typeId: string, size: string | null | undefined, excludeConsumed: boolean) =>
      availableProducts.filter(p =>
        p.product_type_id === typeId &&
        p.inventory_type === 'individual' &&
        !!p.item_id &&
        (!size || p.size === size) &&
        (!excludeConsumed || !consumedItemIds.has(p.item_id!))
      );
    const remainingIndividualItems = (typeId: string, size?: string | null) =>
      individualItems(typeId, size, true);

    // Availability of a single bundle component, with or without cart consumption.
    const componentRemaining = (comp: CartComponent, includeCart: boolean): number => {
      const rows = availableProducts.filter(p => p.product_type_id === comp.product_type_id);
      if (rows.length === 0) return 0;
      const invType = rows[0].inventory_type;
      if (invType === 'unlimited') return Infinity;
      if (invType === 'pool') return includeCart ? remainingPool(comp.product_type_id) : poolCapacity(comp.product_type_id);
      // individual
      const items = individualItems(comp.product_type_id, comp.size || undefined, includeCart);
      // Each bundle consumes one item of ANY size (when size is unspecified), so
      // the bundle count is bounded by the TOTAL remaining items, not the largest
      // single-size group. individualItems already filters to comp.size when set.
      return items.length;
    };

    // How many whole bundles the components can fulfill: MIN over components of
    // floor(component_available / component_quantity). `includeCart=false` gives
    // the base feasibility (used to hide structurally-unfulfillable bundles).
    const bundleCount = (bundle: { bundle_components: unknown }, includeCart: boolean): number => {
      const comps = bundle.bundle_components as CartComponent[] | null;
      if (!Array.isArray(comps) || comps.length === 0) return 0;
      let min = Infinity;
      for (const comp of comps) {
        min = Math.min(min, Math.floor(componentRemaining(comp, includeCart) / (comp.quantity || 1)));
        if (min === 0) break;
      }
      return min;
    };

    return {
      consumedItemIds,
      consumedPool: (typeId: string) => consumedPoolByType.get(typeId) || 0,
      remainingPool,
      remainingIndividualItems,
      bundleRemaining: (bundle: { bundle_components: unknown }) => bundleCount(bundle, true),
      bundleRemainingBase: (bundle: { bundle_components: unknown }) => bundleCount(bundle, false),
    };
  }, [availableProducts, selectedProducts, selectedAddons]);

  // Add product to selection
  const handleAddProduct = async (product: AvailableProduct) => {
    if (!selectedLocation) return;

    // Get product type details for validation
    const typeDetails = productTypeDetails.get(product.product_type_id);
    const inventoryType = typeDetails?.inventory_type || product.inventory_type;

    // ── Bundle handling ──
    if (product.is_bundle && product.bundle_components) {
      const components = product.bundle_components as Array<{ product_type_id: string; quantity: number; size?: string | null }>;

      // Price the bundle itself
      const { data: pricingData, error: pricingError } = await calculatePrice(
        product.product_type_id,
        selectedLocation.id,
        durationDays,
        durationHours,
        null,
        'admin',
        calculateElapsedHours()
      );
      if (pricingError || !pricingData) {
        setError(pricingError || 'Unable to calculate price for this bundle');
        return;
      }

      // Resolve each component's availability and assign inventory items.
      // Exclude everything the cart already holds (standalone items + other bundles'
      // individual components) so a component can't be assigned a taken item_id.
      const excludedIds = [...cartAvailability.consumedItemIds];
      // Pool inventory reserved while resolving THIS bundle, keyed by component
      // product_type_id, so two pool components of the same type don't both claim
      // the same units (on top of what the cart already reserves).
      const poolReservedThisBundle = new Map<string, number>();
      const resolvedComponents: BundleComponentResolved[] = [];

      // Use the SAME UTC window as the main availability fetch and the booking
      // insert. Passing a naive local wall-time string here made the DB interpret
      // it in UTC, shifting the window by the merchant's offset and letting items
      // that are actually booked look free — only to be rejected by the overlap
      // trigger at insert time.
      const bundleStartIso = useNow
        ? new Date().toISOString()
        : zonedWallTimeToUtc(startDate, startTime, timezone).toISOString();
      const bundleEndIso = calculateEndDateTime() || '';

      for (const comp of components) {
        const needed = comp.quantity || 1;

        // Fetch component availability (with current exclusions)
        const { data: compAvail, error: compError } = await checkAvailability({
          locationId: selectedLocation.id,
          startDate: bundleStartIso,
          endDate: bundleEndIso,
          productTypeId: comp.product_type_id,
          size: comp.size || undefined,
          excludedInventoryItemIds: excludedIds.length > 0 ? excludedIds : undefined,
          skipTimeGates: true,
        });

        if (compError || !compAvail) {
          setError(`Bundle component unavailable: ${compError || 'No availability data'}`);
          return;
        }

        const compInventoryType = compAvail[0]?.inventory_type || 'individual';
        const compProductName = compAvail[0]?.product_name || 'Component';

        if (compInventoryType === 'individual') {
          const availableItems = comp.size
            ? compAvail.filter(i => i.size === comp.size)
            : compAvail;
          if (availableItems.length < needed) {
            setError(`Not enough ${compProductName} available for this bundle (need ${needed}, have ${availableItems.length})`);
            return;
          }
          const assignedIds = availableItems.slice(0, needed).map(i => i.item_id!);
          excludedIds.push(...assignedIds);
          resolvedComponents.push({
            product_type_id: comp.product_type_id,
            product_name: compProductName,
            quantity: needed,
            size: comp.size || availableItems[0]?.size || null,
            inventoryItemIds: assignedIds,
            inventoryType: 'individual',
          });
        } else if (compInventoryType === 'pool') {
          const poolQty = compAvail[0]?.pool_quantity || 0;
          const alreadyReserved =
            cartAvailability.consumedPool(comp.product_type_id) +
            (poolReservedThisBundle.get(comp.product_type_id) || 0);
          const remaining = poolQty - alreadyReserved;
          if (remaining < needed) {
            setError(`Not enough ${compProductName} available for this bundle (need ${needed}, have ${Math.max(0, remaining)})`);
            return;
          }
          poolReservedThisBundle.set(
            comp.product_type_id,
            (poolReservedThisBundle.get(comp.product_type_id) || 0) + needed
          );
          resolvedComponents.push({
            product_type_id: comp.product_type_id,
            product_name: compProductName,
            quantity: needed,
            size: comp.size || null,
            inventoryItemIds: [],
            inventoryType: 'pool',
          });
        } else {
          // unlimited
          resolvedComponents.push({
            product_type_id: comp.product_type_id,
            product_name: compProductName,
            quantity: needed,
            size: comp.size || null,
            inventoryItemIds: [],
            inventoryType: 'unlimited',
          });
        }
      }

      const newProduct: SelectedProduct = {
        product_type_id: product.product_type_id,
        productName: product.product_name,
        size: null,
        quantity: 1,
        pricePerUnit: pricingData.price,
        ruleColumns: pricingRuleColumns(pricingData.rule),
        inventoryItemId: null, // bundles have no direct inventory
        inventoryType: product.inventory_type,
        isBundle: true,
        bundleComponents: resolvedComponents,
      };

      setSelectedProducts([...selectedProducts, newProduct]);
      return;
    }

    // ── Non-bundle handling (existing logic) ──

    // Validate availability against the single cart-aware source of truth.
    // For individual inventory, also capture the specific item to assign so a
    // bundle component and a standalone line can never grab the same item_id.
    let assignFromItem: AvailableProduct | undefined;
    if (inventoryType === 'pool') {
      const poolQuantity = typeDetails?.pool_quantity || 0;
      if (cartAvailability.remainingPool(product.product_type_id) <= 0) {
        setError(t('newBooking.cannotAddMorePool', { product: product.product_name, count: poolQuantity }));
        return;
      }
    } else if (inventoryType === 'individual') {
      // Next item not already consumed by any cart line (standalone OR bundle).
      const remainingItems = cartAvailability.remainingIndividualItems(product.product_type_id, product.size);
      if (remainingItems.length <= 0) {
        const sizeKey = product.size || t('newBooking.oneSize');
        setError(t('newBooking.cannotAddMore', { product: product.product_name, size: sizeKey }));
        return;
      }
      assignFromItem = remainingItems[0];
    }
    // For 'unlimited' inventory, no validation needed

    console.log('📦 Adding product:', product.product_name, 'Size:', product.size);
    console.log('🔍 item_id from availability:', product.item_id);
    console.log('🔍 inventory_type:', product.inventory_type);

    // Calculate price based on pricing rules
    const { data: pricingData, error: pricingError } = await calculatePrice(
      product.product_type_id,
      selectedLocation.id,
      durationDays,
      durationHours,
      product.size,
      'admin',
      calculateElapsedHours()
    );

    if (pricingError || !pricingData) {
      console.error('❌ Pricing error:', pricingError);
      setError(pricingError || 'Unable to calculate price for this product');
      return;
    }

    console.log('💰 Price calculated:', pricingData.price, currencySymbol, 'using rule:', pricingData.rule.duration_label);

    // For individual inventory, assign the item chosen above (the first not-yet-
    // consumed item, preserving the allocation order from check_availability_enhanced).
    let assignedItemId = product.item_id;
    if (inventoryType === 'individual') {
      assignedItemId = assignFromItem?.item_id ?? product.item_id;
    }

    const newProduct: SelectedProduct = {
      product_type_id: product.product_type_id,
      productName: product.product_name,
      size: product.size,
      quantity: 1,
      pricePerUnit: pricingData.price,
      ruleColumns: pricingRuleColumns(pricingData.rule),
      inventoryItemId: assignedItemId,
      inventoryType: product.inventory_type,
    };

    if (!newProduct.inventoryItemId && product.inventory_type === 'individual') {
      console.error('❌ CRITICAL: Individual inventory item has no item_id!');
      setError('Cannot add product: No specific inventory item assigned. Please try again.');
      return;
    }

    setSelectedProducts([...selectedProducts, newProduct]);
  };

  // Remove product from selection
  const handleRemoveProduct = (index: number) => {
    setSelectedProducts(selectedProducts.filter((_, i) => i !== index));
  };

  // Update product quantity
  const handleUpdateQuantity = (index: number, quantity: number) => {
    const product = selectedProducts[index];

    // Get product type details for validation
    const typeDetails = productTypeDetails.get(product.product_type_id);
    const inventoryType = typeDetails?.inventory_type || product.inventoryType;

    let maxAllowed: number;

    // Validate based on inventory type. Remaining helpers already subtract this
    // line's own consumption, so add its current quantity back to get this line's
    // ceiling (counting other standalone lines AND bundle components of the type).
    if (inventoryType === 'pool') {
      maxAllowed = cartAvailability.remainingPool(product.product_type_id) + product.quantity;
      if (quantity > maxAllowed) {
        setError(`Only ${maxAllowed} ${product.productName} available in pool.`);
      }
    } else if (inventoryType === 'individual') {
      maxAllowed = cartAvailability.remainingIndividualItems(product.product_type_id, product.size).length + product.quantity;
      if (quantity > maxAllowed) {
        setError(`Only ${maxAllowed} ${product.productName}${product.size ? ` (${product.size})` : ''} available.`);
      }
    } else {
      // For 'unlimited' inventory, no limit
      maxAllowed = quantity;
    }

    const newQuantity = Math.max(1, Math.min(quantity, maxAllowed));

    const updated = [...selectedProducts];
    updated[index].quantity = newQuantity;
    setSelectedProducts(updated);
  };

  // Add addon to selection
  const handleAddAddon = async (product: AvailableProduct) => {
    if (!selectedLocation) return;

    // Get product type details for validation
    const typeDetails = productTypeDetails.get(product.product_type_id);
    const inventoryType = typeDetails?.inventory_type || product.inventory_type;

    // Validate availability against the single cart-aware source of truth.
    let assignFromItem: AvailableProduct | undefined;
    if (inventoryType === 'pool') {
      const poolQuantity = typeDetails?.pool_quantity || 0;
      if (cartAvailability.remainingPool(product.product_type_id) <= 0) {
        setError(t('newBooking.cannotAddMorePool', { product: product.product_name, count: poolQuantity }));
        return;
      }
    } else if (inventoryType === 'individual') {
      // Next item not already consumed by any cart line (standalone OR bundle).
      const remainingItems = cartAvailability.remainingIndividualItems(product.product_type_id, product.size);
      if (remainingItems.length <= 0) {
        const sizeKey = product.size || t('newBooking.oneSize');
        setError(t('newBooking.cannotAddMore', { product: product.product_name, size: sizeKey }));
        return;
      }
      assignFromItem = remainingItems[0];
    }
    // For 'unlimited' inventory, no validation needed

    console.log('➕ Adding addon:', product.product_name, 'Size:', product.size);
    console.log('🔍 item_id from availability:', product.item_id);
    console.log('🔍 inventory_type:', product.inventory_type);

    // Calculate price based on pricing rules
    const { data: pricingData, error: pricingError } = await calculatePrice(
      product.product_type_id,
      selectedLocation.id,
      durationDays,
      durationHours,
      product.size,
      'admin',
      calculateElapsedHours()
    );

    if (pricingError || !pricingData) {
      console.error('❌ Pricing error:', pricingError);
      setError(pricingError || 'Unable to calculate price for this addon');
      return;
    }

    console.log('💰 Addon price calculated:', pricingData.price, currencySymbol, 'using rule:', pricingData.rule.duration_label);

    // For individual inventory, assign the item chosen above (the first not-yet-
    // consumed item, preserving the allocation order from check_availability_enhanced).
    let assignedItemId = product.item_id;
    if (inventoryType === 'individual') {
      assignedItemId = assignFromItem?.item_id ?? product.item_id;
    }

    const newAddon: SelectedProduct = {
      product_type_id: product.product_type_id,
      productName: product.product_name,
      size: product.size,
      quantity: 1,
      pricePerUnit: pricingData.price,
      ruleColumns: pricingRuleColumns(pricingData.rule),
      inventoryItemId: assignedItemId,
      inventoryType: product.inventory_type,
    };

    if (!newAddon.inventoryItemId && product.inventory_type === 'individual') {
      console.error('❌ CRITICAL: Individual inventory item has no item_id!');
      setError('Cannot add addon: No specific inventory item assigned. Please try again.');
      return;
    }

    setSelectedAddons([...selectedAddons, newAddon]);
  };

  // Remove addon from selection
  const handleRemoveAddon = (index: number) => {
    setSelectedAddons(selectedAddons.filter((_, i) => i !== index));
  };

  // Update addon quantity
  const handleUpdateAddonQuantity = (index: number, quantity: number) => {
    const addon = selectedAddons[index];

    // Get product type details for validation
    const typeDetails = productTypeDetails.get(addon.product_type_id);
    const inventoryType = typeDetails?.inventory_type || addon.inventoryType;

    let maxAllowed: number;

    // Validate based on inventory type. Remaining helpers already subtract this
    // line's own consumption, so add its current quantity back to get its ceiling
    // (counting other standalone lines AND bundle components of the type).
    if (inventoryType === 'pool') {
      maxAllowed = cartAvailability.remainingPool(addon.product_type_id) + addon.quantity;
      if (quantity > maxAllowed) {
        setError(`Only ${maxAllowed} ${addon.productName} available in pool.`);
      }
    } else if (inventoryType === 'individual') {
      maxAllowed = cartAvailability.remainingIndividualItems(addon.product_type_id, addon.size).length + addon.quantity;
      if (quantity > maxAllowed) {
        setError(`Only ${maxAllowed} ${addon.productName}${addon.size ? ` (${addon.size})` : ''} available.`);
      }
    } else {
      // For 'unlimited' inventory, no limit
      maxAllowed = quantity;
    }

    const newQuantity = Math.max(1, Math.min(quantity, maxAllowed));

    const updated = [...selectedAddons];
    updated[index].quantity = newQuantity;
    setSelectedAddons(updated);
  };

  // Handle address input change (with autocomplete)
  const handleAddressInputChange = async (value: string) => {
    setDeliveryAddressInput(value);
    setValidatedAddress(null); // Reset validated address

    if (value.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setLoadingAddress(true);
    const { data, error: autocompleteError } = await getAddressAutocomplete(value);

    if (!autocompleteError && data) {
      setAddressSuggestions(data);
    }
    setLoadingAddress(false);
  };

  // Handle address selection from autocomplete
  const handleSelectAddress = async (suggestion: AddressSuggestion) => {
    setDeliveryAddressInput(suggestion.description);
    setAddressSuggestions([]);
    setLoadingAddress(true);

    // Validate the selected address
    const { data, error: validationError } = await validateAddress(
      suggestion.place_id
    );

    if (validationError) {
      setError(`Failed to validate address: ${validationError}`);
      setLoadingAddress(false);
      return;
    }

    if (data) {
      setValidatedAddress(data);

      // Calculate delivery price if we have location coordinates
      if (selectedLocation) {
        await calculateDelivery(data);
      }
    }
    setLoadingAddress(false);
  };

  // Calculate delivery price
  const calculateDelivery = async (destination: ValidatedAddress) => {
    if (!selectedLocation) return;

    setLoadingDeliveryPrice(true);

    try {
      // Get origin coordinates from delivery_origin address (preferred) or lat/lng (fallback)
      let originLat: number | null = null;
      let originLng: number | null = null;

      // @ts-ignore - delivery_origin may not be in type definition yet
      if (selectedLocation.delivery_origin) {
        // Geocode the delivery_origin address to get coordinates
        // @ts-ignore
        console.log('Using delivery_origin address:', selectedLocation.delivery_origin);

        try {
          // Get address suggestions for the delivery origin
          // @ts-ignore
          const { data: suggestions } = await getAddressAutocomplete(
            selectedLocation.delivery_origin
          );

          if (suggestions && suggestions.length > 0) {
            // Use the first suggestion (most relevant)
            const { data: originAddress } = await validateAddress(
              suggestions[0].place_id
            );

            if (originAddress && originAddress.lat && originAddress.lng) {
              originLat = originAddress.lat;
              originLng = originAddress.lng;
              console.log('✅ Geocoded delivery_origin:', { lat: originLat, lng: originLng });
            }
          }
        } catch (geocodeError) {
          console.error('⚠️  Failed to geocode delivery_origin:', geocodeError);
        }
      }

      // Fallback to latitude/longitude if delivery_origin geocoding failed
      if (!originLat || !originLng) {
        originLat = selectedLocation.latitude;
        originLng = selectedLocation.longitude;
        console.log('Using direct coordinates from location:', { lat: originLat, lng: originLng });
      }

      // Check if we have valid origin coordinates
      if (!originLat || !originLng) {
        setError(
          'Location delivery origin not configured. Please add a delivery_origin address or coordinates to this location.'
        );
        console.error('❌ Missing delivery origin:', {
          locationId: selectedLocation.id,
          locationName: selectedLocation.location_name,
          // @ts-ignore
          delivery_origin: selectedLocation.delivery_origin,
          latitude: selectedLocation.latitude,
          longitude: selectedLocation.longitude,
        });
        setLoadingDeliveryPrice(false);
        return;
      }

      const { data, error: priceError } = await calculateDeliveryPrice({
        originLat,
        originLng,
        destinationLat: destination.lat,
        destinationLng: destination.lng,
        locationId: selectedLocation.id,
      });

      if (priceError) {
        setError(`Failed to calculate delivery price: ${priceError}`);
        setLoadingDeliveryPrice(false);
        return;
      }

      if (data) {
        setDeliveryDistance(data.distance_km);
        setDeliveryPrice(data.total_price);
      }
    } catch (error) {
      console.error('❌ Delivery calculation error:', error);
      setError('Failed to calculate delivery price. Please try again.');
    } finally {
      setLoadingDeliveryPrice(false);
    }
  };

  // Submit booking
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    if (!selectedLocation) {
      setError(t('newBooking.errorInvalidBooking'));
      setLoading(false);
      return;
    }

    let start: string;
    let end: string;

    if (useNow) {
      // For "now" bookings, recalculate current time
      const now = new Date();
      start = now.toISOString();

      const calculatedEnd = calculateEndDateTime();
      if (!calculatedEnd) {
        setError(t('newBooking.errorInvalidBooking'));
        setLoading(false);
        return;
      }
      end = calculatedEnd;
    } else {
      // For scheduled bookings, use selected date/time interpreted in the
      // merchant's timezone (NOT the admin's browser timezone).
      const startDateTime = zonedWallTimeToUtc(startDate, startTime, timezone);
      if (isNaN(startDateTime.getTime())) {
        setError(t('newBooking.errorInvalidBooking'));
        setLoading(false);
        return;
      }
      start = startDateTime.toISOString();

      const calculatedEnd = calculateEndDateTime();

      if (!calculatedEnd) {
        setError(t('newBooking.errorInvalidBooking'));
        setLoading(false);
        return;
      }

      end = calculatedEnd;
    }

    // Hard guard: the DB has a check_dates constraint requiring end > start.
    // Surface a clear UI error if the computed end is somehow at/before start,
    // rather than letting Postgres reject the insert with an opaque message.
    if (new Date(end).getTime() <= new Date(start).getTime()) {
      setError(t('newBooking.errorEndBeforeStart'));
      setLoading(false);
      return;
    }

    const discountAmount = discountValidation?.valid ? discountValidation.discount : 0;

    // Calculate tax. Shares computeBookingTotals with every edit path so the
    // split a booking is created with is the same one an edit recomputes —
    // the divergence between the two is what silently zeroed VAT on edit.
    // calculateTotal() already folds in delivery and the discount, so it is
    // the helper's `base`; the components are passed through individually.
    const taxRate = selectedLocation.tax_rate || 0;
    const taxIncluded = selectedLocation.tax_included ?? true;
    const baseTotal = calculateTotal();
    const {
      subtotal: baseSubtotal,
      totalTax,
      totalAmount: computedTotalAmount,
    } = computeBookingTotals({
      subtotal: baseTotal - (deliveryPrice || 0) + discountAmount,
      deliveryPrice,
      totalDiscount: discountAmount,
      taxRate,
      taxIncluded,
    });

    const paymentState = effectivePaymentMethod === 'paid' ? 'paid' : effectivePaymentMethod === 'link' ? 'open' : 'open_pay_later';

    const bookingData = {
      merchant_id: selectedLocation.merchant_id,
      location_id: selectedLocation.id,
      start_date: start,
      end_date: end,
      customer_firstname: customerFirstName,
      customer_lastname: customerLastName,
      customer_email: customerEmail,
      customer_phone: customerPhone,
      booking_state: 'open',
      payment_state: paymentState,
      amount_paid: effectivePaymentMethod === 'paid' ? computedTotalAmount : 0,
      subtotal: baseSubtotal,
      total_amount: computedTotalAmount,
      tax_rate: taxRate || null,
      tax_included: taxIncluded,
      total_tax: totalTax || null,
      total_discount: discountAmount,
      discount_code_id: discountValidation?.discountCodeId || null,
      discount_code_used: discountValidation?.valid ? discountCode : null,
      delivery_street: formatStreetAddress(validatedAddress),
      delivery_city: validatedAddress?.city || null,
      delivery_zip: validatedAddress?.zip_code || null,
      delivery_country: validatedAddress?.country || null,
      delivery_price: deliveryPrice || null,
      delivery_distance_meters: deliveryDistance ? Math.round(deliveryDistance * 1000) : null,
      delivery_notes: deliveryNotes || null,
      internal_notes: internalNotes || null,
      currency: currencyCode,
      language: customerLanguage || 'en',
      sales_channel: 'admin',
      marketing_consent: customerMarketingConsent,
      custom_field_values: Object.keys(customFieldValues).some(k => customFieldValues[k]?.trim()) ? customFieldValues : null,
    };

    // Add duration_hours to each item
    const totalDurationHours = calculateTotalDurationHours();
    const bookingItems = [...selectedProducts, ...selectedAddons].map(item => ({
      ...item,
      durationHours: totalDurationHours,
    }));

    const { data, error: createError } = await createBooking(bookingData as any, bookingItems);

    if (createError) {
      setError(createError);
      setLoading(false);
      return;
    }

    // Record the payment in the ledger when the operator marked the booking as
    // already paid. The booking row itself already carries amount_paid and
    // payment_state (set above), so this writes the payment_transactions row only —
    // without it the money is invisible to every surface that reads that table,
    // including platform-fee invoicing and the payment-method breakdown.
    //
    // Channel is 'manual' because this flow doesn't ask how the customer paid; the
    // operator is recording that payment happened, not how. If a merchant is ever
    // billed a percentage fee on manual-channel payments this becomes ambiguous and
    // the flow needs an explicit channel picker.
    //
    // Deliberately not fatal: the booking exists and is correctly marked paid. A
    // failure here leaves the pre-existing behaviour (paid booking, no ledger row),
    // which an operator can repair from the booking's financial view.
    if (data && effectivePaymentMethod === 'paid') {
      const paidAmount = computedTotalAmount;
      if (paidAmount > 0) {
        const { error: ledgerError } = await recordManualPayment({
          bookingId: data.id,
          amount: paidAmount,
          channel: 'manual',
          notes: 'Marked as paid at booking creation (admin)',
          userId: user?.id,
          skipBookingUpdate: true,
        });
        if (ledgerError) {
          console.error('Failed to record payment transaction for new booking:', ledgerError);
        }
      }
    }

    // Log booking creation to audit log (fire-and-forget)
    if (data) {
      insertAuditLog({
        bookingId: data.id,
        merchantId: selectedLocation.merchant_id,
        locationId: selectedLocation.id,
        changeType: 'created',
        changeDetails: {
          booking_state: 'open',
          payment_state: paymentState,
          sales_channel: 'admin',
        },
        userId: user?.id,
        userEmail: profile?.full_name || user?.email || null,
        triggeredBy: 'admin_panel',
      });
    }

    // Update discount code usage if applied
    if (discountValidation?.valid && discountValidation.discountCodeId && discountAmount > 0) {
      await incrementDiscountCodeUsage(discountValidation.discountCodeId, discountAmount);
    }

    // Trigger Make.com webhook for all new bookings (regardless of payment method).
    // The webhook handles instant booking confirmations (booking_time + 0 min)
    // for both WhatsApp and SMS channels.
    if (data) {
      const loc = selectedLocation as any;
      const sendNow = determineSendNow(loc, start);
      const paymentFlow = effectivePaymentMethod === 'later' ? 'pay_later' : effectivePaymentMethod;
      const webhookPayload = {
        booking_id: data.id,
        end_date: end,
        send_now: sendNow,
        booking_state: 'open',
        payment_state: paymentState,
        payment_flow: paymentFlow,
        language: customerLanguage || 'en',
        self_service_enabled: loc.self_service_enabled ?? false,
        deliveries: loc.deliveries ?? false,
        custom_fields: Object.keys(customFieldValues).some(k => customFieldValues[k]?.trim()) ? customFieldValues : null,
      };

      // Fire-and-forget: don't block navigation on webhook result
      fetch(BOOKING_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload),
      }).catch((err) => {
        console.error('Failed to trigger booking webhook:', err);
      });
    }

    // Success! Navigate to bookings list
    navigate('/bookings');
  };

  // Bundles to hide entirely: their components can't fulfill even one bundle
  // regardless of the cart (structural infeasibility). Cart-driven exhaustion is
  // handled by disabling the button at remaining 0, not by hiding the product.
  const unavailableBundleIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of availableProducts.filter(p => p.is_bundle && p.bundle_components)) {
      if (cartAvailability.bundleRemainingBase(p) < 1) ids.add(p.product_type_id);
    }
    return ids;
  }, [availableProducts, cartAvailability]);

  // Group products by category and size (only rentable products, excluding addons)
  // Explicitly exclude products with product_type === 'addon'
  // Also include 'unknown' types as they are likely rentable products with missing type info
  const searchLower = productSearchQuery.toLowerCase().trim();
  const groupedProducts = availableProducts
    .filter(product => {
      if (!product.product_type) return false; // Exclude products without type
      if (product.product_type === 'addon') return false; // EXPLICITLY exclude addons
      if (!isActiveProduct(productTypeDetails.get(product.product_type_id))) return false;
      // Hide bundles whose components are unavailable
      if (product.is_bundle && unavailableBundleIds.has(product.product_type_id)) return false;
      // Include products in the rentable types list OR unknown types (which are likely rentable)
      if (!RENTABLE_PRODUCT_TYPES.includes(product.product_type) && product.product_type !== 'unknown') return false;
      // Apply search filter
      if (searchLower && !product.product_name.toLowerCase().includes(searchLower)) return false;
      // Apply category filter (multi-select: product must match any selected category)
      if (selectedCategoryIds.length > 0 && (!product.category_id || !selectedCategoryIds.includes(product.category_id))) return false;
      return true;
    })
    .reduce((acc, product) => {
      const key = product.product_name;
      if (!acc[key]) {
        acc[key] = {
          name: product.product_name,
          picture_url: product.picture_url,
          hidden: true,
          sizes: {},
        };
      }
      // Products sharing a name are grouped; badge the card only if all of them are hidden
      if (!isHiddenProduct(productTypeDetails.get(product.product_type_id))) acc[key].hidden = false;

      const size = product.size || 'One Size';
      if (!acc[key].sizes[size]) {
        acc[key].sizes[size] = [];
      }
      acc[key].sizes[size].push(product);

      return acc;
    }, {} as Record<string, { name: string; picture_url: string | null; hidden: boolean; sizes: Record<string, AvailableProduct[]> }>);

  // Group addon products separately for Step 4
  // Explicitly include products with product_type === 'addon'
  // OR anything NOT in the rentable types list (helmet, lock, etc.)
  // BUT exclude 'unknown' types (those are treated as products)
  const groupedAddons = availableProducts
    .filter(product => {
      if (!product.product_type) return false; // Exclude products without type
      if (product.product_type === 'unknown') return false; // Exclude unknown types (treated as products)
      if (!isActiveProduct(productTypeDetails.get(product.product_type_id))) return false;
      // EXPLICITLY include 'addon' type OR products not in rentable list
      return product.product_type === 'addon' || !RENTABLE_PRODUCT_TYPES.includes(product.product_type);
    })
    .reduce((acc, product) => {
      const key = product.product_name;
      if (!acc[key]) {
        acc[key] = {
          name: product.product_name,
          picture_url: product.picture_url,
          hidden: true,
          sizes: {},
        };
      }
      // Products sharing a name are grouped; badge the card only if all of them are hidden
      if (!isHiddenProduct(productTypeDetails.get(product.product_type_id))) acc[key].hidden = false;

      const size = product.size || 'One Size';
      if (!acc[key].sizes[size]) {
        acc[key].sizes[size] = [];
      }
      acc[key].sizes[size].push(product);

      return acc;
    }, {} as Record<string, { name: string; picture_url: string | null; hidden: boolean; sizes: Record<string, AvailableProduct[]> }>);

  return (
    <div className="space-y-xl animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-primary mb-sm">{t('newBooking.title')}</h1>
        <p className="text-secondary">{t('newBooking.subtitle')}</p>
      </div>

      {/* Progress Steps */}
      <div className="card">
        {/*
          UX: Make stepper resilient on small screens.
          - Mobile: horizontal scroll, no forced equal-width columns (prevents label wrapping + misalignment)
          - Desktop: stretches nicely across the row
        */}
        <div
          ref={stepperRef}
          className="flex items-center gap-sm overflow-x-auto pb-sm -mx-sm px-sm md:mx-0 md:px-0 md:overflow-visible md:justify-between snap-x snap-mandatory"
          data-testid="new-booking-progress-steps"
        >
          {BASE_STEPS
            .filter(step => !step.requiresDelivery || selectedLocation?.deliveries)
            .map((step, index, filteredSteps) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <div
                  key={step.id}
                  ref={(el) => { stepItemRefs.current[step.id] = el; }}
                  className="flex items-center flex-none md:flex-1 snap-center"
                  data-testid={`new-booking-progress-step-${step.id}`}
                >
                  <div className="flex flex-col items-center flex-none md:flex-1 min-w-[92px] px-sm">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        isCompleted
                          ? 'bg-success text-white'
                          : isActive
                          ? 'bg-blue text-white'
                          : 'bg-secondary/10 text-secondary'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <span
                      className={`text-xs mt-xs font-medium whitespace-nowrap ${
                        isActive ? 'text-primary' : 'text-secondary'
                      }`}
                    >
                      {t(`newBooking.step${step.id}Name`)}
                    </span>
                  </div>

                  {/* Connector (hidden on mobile to avoid odd stretching while scrolling) */}
                  {index < filteredSteps.length - 1 && (
                    <div
                      className={`hidden md:block h-0.5 flex-1 mx-sm transition-colors ${
                        isCompleted ? 'bg-success' : 'bg-secondary/20'
                      }`}
                      aria-hidden="true"
                    />
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* Navigation Buttons - At top for easy access */}
      <div className="page-header" data-testid="new-booking-nav-header">
        <button
          type="button"
          onClick={handleBack}
          disabled={currentStep <= firstStep}
          className="btn btn-ghost"
          data-testid="new-booking-back-button"
        >
          <ChevronLeft className="w-4 h-4" />
          {t('common.back')}
        </button>

        <div className="page-header-actions" data-testid="new-booking-nav-actions">
          <button
            type="button"
            onClick={() => navigate('/bookings')}
            className="btn btn-ghost"
            data-testid="new-booking-cancel-button"
          >
            {t('common.cancel')}
          </button>

          {currentStep < 7 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || loadingAvailability}
              className="btn btn-primary"
              data-testid="new-booking-next-button"
            >
              {loadingAvailability ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('newBooking.checking')}
                </>
              ) : (
                <>
                  {t('newBooking.next')}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="btn btn-primary"
              data-testid="new-booking-create-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('newBooking.creating')}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {t('newBooking.createBooking')}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg">
          <div className="flex items-start gap-md">
            <AlertTriangle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-danger mb-xs">{t('common.error')}</h4>
              <p className="text-sm text-secondary">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="card">
        {/* STEP 1: Location Selection */}
        {currentStep === 1 && (
          <div className="space-y-lg">
            <div className="flex items-center gap-md mb-lg">
              <MapPin className="w-6 h-6 text-blue" />
              <h2 className="text-2xl font-bold text-primary">{t('newBooking.selectLocation')}</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-2xl">
                <Loader2 className="w-8 h-8 text-blue animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-lg">
                {locations.map((location) => (
                  <button
                    key={location.id}
                    type="button"
                    onClick={() => setSelectedLocation(location)}
                    className={`p-lg border-2 rounded-xl text-left transition-all hover:shadow-lg ${
                      selectedLocation?.id === location.id
                        ? 'border-blue bg-blue/5 shadow-md'
                        : 'border-border hover:border-blue/50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-md">
                      <MapPin
                        className={`w-6 h-6 ${
                          selectedLocation?.id === location.id ? 'text-blue' : 'text-secondary'
                        }`}
                      />
                      {selectedLocation?.id === location.id && (
                        <Check className="w-5 h-5 text-blue" />
                      )}
                    </div>
                    <h3 className="font-semibold text-primary text-lg mb-xs">
                      {location.location_name}
                    </h3>
                    <p className="text-sm text-secondary mb-md">{stripHtml(location.pickup_address)}</p>
                    {location.deliveries && (
                      <span className="inline-flex items-center gap-xs px-sm py-xs bg-blue/10 text-blue text-xs font-medium rounded-full">
                        <Truck className="w-3 h-3" />
                        {t('newBooking.deliveryAvailable')}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Date & Time Selection */}
        {currentStep === 2 && (
          <div className="space-y-lg">
            <div className="flex items-center gap-md mb-lg">
              <Calendar className="w-6 h-6 text-blue" />
              <h2 className="text-2xl font-bold text-primary">{t('newBooking.selectDateTime')}</h2>
            </div>

            {/* "Now" Toggle */}
            <div className="flex items-center gap-md p-md bg-secondary/5 rounded-lg border border-border">
              <input
                type="checkbox"
                id="use-now"
                checked={useNow}
                onChange={(e) => setUseNow(e.target.checked)}
                className="w-5 h-5 text-blue rounded focus:ring-blue"
              />
              <label htmlFor="use-now" className="flex items-center gap-sm cursor-pointer">
                <Zap className="w-5 h-5 text-warning" />
                <span className="font-medium text-primary">{t('newBooking.startNow')}</span>
              </label>
            </div>

            {!useNow && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('newBooking.startDate')} <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="input pl-10 w-full h-12 text-base md:h-14 md:text-lg"
                      data-testid="new-booking-start-date-input"
                    />
                  </div>
                </div>

                {/* Start Time */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('newBooking.startTime')} <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="input pl-10 w-full h-12 text-base md:h-14 md:text-lg"
                      data-testid="new-booking-start-time-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-primary mb-md">
                {t('newBooking.rentalDuration')} <span className="text-danger">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                <div>
                  <label className="block text-xs text-secondary mb-xs">{t('newBooking.days')}</label>
                  <NumberInput
                    value={durationDays}
                    onChange={(v) => {
                      setDurationDays(v);
                      // Hours acts as a full exact span only when Days is 0.
                      // Once Days > 0 it reverts to a 0–23 remainder, so clamp
                      // any spillover value down instead of blocking the Days
                      // field (which would leave the user unsure how to proceed).
                      if (v > 0 && durationHours > 23) {
                        setDurationHours(23);
                      }
                    }}
                    mode="int"
                    min={0}
                    className="input w-full h-12 text-base"
                    data-testid="new-booking-duration-days-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-secondary mb-xs">{t('newBooking.hours')}</label>
                  {/* When Days is 0 the Hours field carries the whole exact span
                      (up to a week, 168h), letting the user book an exact 24h/48h/…
                      rental distinct from an N-day "until closing" booking. Once
                      Days > 0, Hours is a 0–23 remainder as before. */}
                  <NumberInput
                    value={durationHours}
                    onChange={(v) => setDurationHours(v)}
                    mode="int"
                    min={0}
                    max={durationDays > 0 ? 23 : 168}
                    className="input w-full h-12 text-base"
                    data-testid="new-booking-duration-hours-input"
                  />
                </div>
              </div>
              <p className="text-sm text-secondary mt-xs">
                {t('newBooking.totalDuration', { duration: formatDuration() })}
              </p>

              {/* Live end time preview */}
              {(durationDays > 0 || durationHours > 0) && (useNow || (startDate && startTime)) && (() => {
                const { end: endStr, adjusted } = calculateEndDateTimeDetailed();
                if (!endStr) return null;
                const endDt = new Date(endStr);

                // Check if return day might be closed (calendar days mode only)
                let closedWarning = false;
                if (durationHours === 0 && durationDays > 0 && selectedLocation?.opening_hours) {
                  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                  // Use merchant-local day-of-week, not browser-local
                  const dayOfWeek = dayNames[asZoned(endDt, timezone).getDay()];
                  const openingHours = selectedLocation.opening_hours as Record<string, { open: string; close: string }>;
                  if (!openingHours[dayOfWeek]?.close) {
                    closedWarning = true;
                  }
                }

                return (
                  <div className="mt-md p-md bg-blue/5 border border-blue/20 rounded-lg">
                    <div className="flex items-center gap-xs text-sm font-medium text-primary">
                      <ArrowRight className="w-4 h-4 text-blue flex-shrink-0" />
                      <span>{t('newBooking.returnLabel')} {formatDateTime(endDt)}</span>
                    </div>
                    {adjusted && (
                      <div className="flex items-center gap-xs text-xs text-warning mt-xs ml-5">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{t('newBooking.endShiftedAfterClosing')}</span>
                      </div>
                    )}
                    {closedWarning && (
                      <div className="flex items-center gap-xs text-xs text-warning mt-xs ml-5">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{t('newBooking.locationMayBeClosed')}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Two-column layout for steps 3 onward */}
        {currentStep >= 3 ? (
          <>
            <div className="flex flex-col lg:flex-row gap-lg" data-testid="new-booking-two-column-layout">
              {/* Main content - flexible width */}
              <div className="flex-1 min-w-0">
                {/* STEP 3: Products with Availability */}
                {currentStep === 3 && (
                  <div className="space-y-lg">
                    <div className="flex items-center gap-md mb-lg">
                      <Package className="w-6 h-6 text-blue" />
                      <h2 className="text-2xl font-bold text-primary">{t('newBooking.selectProducts')}</h2>
                    </div>

                    {/* Search bar */}
                    {!loadingAvailability && availableProducts.length > 0 && (
                      <div className="space-y-sm">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary" />
                          <input
                            type="text"
                            placeholder={t('newBooking.searchProducts')}
                            value={productSearchQuery}
                            onChange={(e) => setProductSearchQuery(e.target.value)}
                            className="input w-full pl-10 h-10 text-sm"
                            data-testid="new-booking-product-search"
                          />
                          {productSearchQuery && (
                            <button
                              type="button"
                              onClick={() => setProductSearchQuery('')}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Category filter chips */}
                        {categoryChips.length > 0 && (
                          <div className="flex flex-wrap gap-sm mt-xs">
                            <button
                              type="button"
                              onClick={() => setSelectedCategoryIds([])}
                              className={`px-lg py-sm rounded-full text-sm font-medium border transition-colors ${
                                selectedCategoryIds.length === 0
                                  ? 'bg-blue text-white border-blue'
                                  : 'bg-white text-secondary border-border hover:border-blue hover:text-blue'
                              }`}
                              data-testid="new-booking-category-chip-all"
                            >
                              {t('newBooking.allCategories')}
                            </button>
                            {categoryChips.map((cat) => {
                              const isSelected = selectedCategoryIds.includes(cat.id);
                              return (
                                <button
                                  key={cat.id}
                                  type="button"
                                  onClick={() => setSelectedCategoryIds(prev =>
                                    isSelected
                                      ? prev.filter(id => id !== cat.id)
                                      : [...prev, cat.id]
                                  )}
                                  className={`px-lg py-sm rounded-full text-sm font-medium border transition-colors ${
                                    isSelected
                                      ? 'bg-blue text-white border-blue'
                                      : 'bg-white text-secondary border-border hover:border-blue hover:text-blue'
                                  }`}
                                  data-testid={`new-booking-category-chip-${cat.id}`}
                                >
                                  {cat.category_name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

              {loadingAvailability ? (
                <div className="flex items-center justify-center py-2xl">
                  <Loader2 className="w-8 h-8 text-blue animate-spin" />
                  <span className="ml-md text-secondary">{t('newBooking.checkingAvailability')}</span>
                </div>
              ) : availableProducts.length === 0 ? (
                <div className="text-center py-2xl">
                  <Package className="w-16 h-16 text-secondary mx-auto mb-md opacity-50" />
                  <p className="text-secondary">{t('newBooking.noProductsAvailable')}</p>
                </div>
              ) : (
                <div className="space-y-md">
                  {/* Availability Legend */}
                  <div className="flex items-center gap-md text-xs text-secondary">
                    <span>{t('newBooking.availability')}</span>
                    <span className="inline-flex items-center gap-xs">
                      <span className="w-3 h-3 rounded bg-green-200 border border-green-300"></span> {t('newBooking.availabilityHigh')}
                    </span>
                    <span className="inline-flex items-center gap-xs">
                      <span className="w-3 h-3 rounded bg-yellow-200 border border-yellow-300"></span> {t('newBooking.availabilityMedium')}
                    </span>
                    <span className="inline-flex items-center gap-xs">
                      <span className="w-3 h-3 rounded bg-red-200 border border-red-300"></span> {t('newBooking.availabilityLow')}
                    </span>
                </div>

                {/* Available Products Grid */}
                {Object.keys(groupedProducts).length === 0 && (productSearchQuery || selectedCategoryIds.length > 0) ? (
                  <div className="text-center py-xl">
                    <Search className="w-10 h-10 text-secondary mx-auto mb-sm opacity-50" />
                    <p className="text-secondary text-sm">{t('newBooking.noFilterMatch')}</p>
                    <button
                      type="button"
                      onClick={() => { setProductSearchQuery(''); setSelectedCategoryIds([]); }}
                      className="text-blue text-sm mt-xs hover:underline"
                    >
                      {t('newBooking.clearFilters')}
                    </button>
                  </div>
                ) : (
                <div className="grid grid-cols-auto-cards gap-md">
                  {Object.entries(groupedProducts).map(([productName, productGroup]) => (
                    <div
                      key={productName}
                      className="border border-border rounded-xl p-md sm:p-lg bg-white shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`new-booking-product-card-${productName}`}
                    >
                      <div className="flex items-start gap-md sm:gap-lg mb-md">
                        {productGroup.picture_url ? (
                          <img
                            src={productGroup.picture_url}
                            alt={productGroup.name}
                            className="w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 object-contain bg-white rounded-lg border border-border flex-shrink-0 p-1 sm:p-2"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-lg bg-secondary/10 border border-border flex items-center justify-center text-secondary flex-shrink-0">
                            <Package className="w-6 h-6 sm:w-7 sm:h-7" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-primary text-sm sm:text-base leading-tight break-words">
                            {productGroup.name}
                          </h3>
                          {productGroup.hidden && (
                            <div className="mt-1">
                              <HiddenProductBadge />
                            </div>
                          )}
                          <p className="text-xs text-secondary mt-1">
                            {t('newBooking.variantsCount', { count: Object.keys(productGroup.sizes).length })}
                          </p>
                        </div>
                      </div>

                      {/* Size Buttons */}
                      <div className="flex flex-wrap gap-sm">
                        {Object.entries(productGroup.sizes).map(([size, items]) => {
                          const firstItem = items[0];
                          const inventoryType = firstItem.inventory_type;

                          // Cart-aware remaining: bundles use MIN over components,
                          // standalone pool/individual subtract what the cart holds.
                          const isUnlimited = firstItem.is_bundle
                            ? cartAvailability.bundleRemaining(firstItem) === Infinity
                            : inventoryType === 'unlimited';
                          const availableCount = isUnlimited
                            ? Infinity
                            : firstItem.is_bundle
                              ? cartAvailability.bundleRemaining(firstItem)
                              : inventoryType === 'pool'
                                ? cartAvailability.remainingPool(firstItem.product_type_id)
                                : cartAvailability.remainingIndividualItems(firstItem.product_type_id, firstItem.size).length;

                          const soldOut = !isUnlimited && availableCount <= 0;
                          const stockLevel = isUnlimited ? 'high' : (availableCount >= 5 ? 'high' : availableCount >= 3 ? 'medium' : availableCount >= 1 ? 'low' : 'low');
                          const badgeStyles = {
                            high: 'bg-green-100 hover:bg-green-200 border-green-300 text-green-800',
                            medium: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300 text-yellow-800',
                            low: 'bg-red-100 hover:bg-red-200 border-red-300 text-red-800'
                          };

                          return (
                            <button
                              key={size}
                              type="button"
                              disabled={soldOut}
                              onClick={() => handleAddProduct(items[0])}
                              className={`inline-flex items-center gap-xs px-md py-2 border rounded-full transition-all font-semibold text-sm ${soldOut ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed opacity-60' : `hover:shadow-md active:scale-[0.98] ${badgeStyles[stockLevel]}`}`}
                              data-testid={`new-booking-product-variant-button-${productName}-${size}`}
                            >
                              <span className="truncate max-w-[10rem]">{size === 'One Size' ? t('newBooking.oneSize') : size}</span>
                              <span className="font-bold flex-shrink-0">({isUnlimited ? '∞' : availableCount})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            )}
                </div>
              )}

              {/* STEP 4: Add-ons */}
              {currentStep === 4 && (
                <div className="space-y-lg">
                  <div className="flex items-center gap-md mb-lg">
                    <Plus className="w-6 h-6 text-blue" />
                    <h2 className="text-2xl font-bold text-primary">{t('newBooking.addonsOptional')}</h2>
                  </div>

            {Object.keys(groupedAddons).length === 0 ? (
              <div className="text-center py-2xl">
                <Plus className="w-16 h-16 text-secondary mx-auto mb-md opacity-50" />
                <p className="text-secondary mb-md">
                  {t('newBooking.noAddonsAvailable')}
                </p>
                <p className="text-sm text-secondary">
                  {t('newBooking.skipStepHint')}
                </p>
              </div>
            ) : (
              <div className="space-y-md">
                {/* Availability Legend */}
                <div className="flex items-center gap-md text-xs text-secondary">
                  <span>{t('newBooking.availability')}</span>
                  <span className="inline-flex items-center gap-xs">
                    <span className="w-3 h-3 rounded bg-green-200 border border-green-300"></span> {t('newBooking.availabilityHigh')}
                  </span>
                  <span className="inline-flex items-center gap-xs">
                    <span className="w-3 h-3 rounded bg-yellow-200 border border-yellow-300"></span> {t('newBooking.availabilityMedium')}
                  </span>
                  <span className="inline-flex items-center gap-xs">
                    <span className="w-3 h-3 rounded bg-red-200 border border-red-300"></span> {t('newBooking.availabilityLow')}
                  </span>
                </div>

                {/* Available Add-ons Grid */}
                <div className="grid grid-cols-auto-cards gap-md">
                  {Object.entries(groupedAddons).map(([addonName, addonGroup]) => (
                    <div
                      key={addonName}
                      className="border border-border rounded-xl p-md sm:p-lg bg-white shadow-sm hover:shadow-md transition-shadow"
                      data-testid={`new-booking-addon-card-${addonName}`}
                    >
                      <div className="flex items-start gap-md sm:gap-lg mb-md">
                        {addonGroup.picture_url ? (
                          <img
                            src={addonGroup.picture_url}
                            alt={addonGroup.name}
                            className="w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 object-contain bg-white rounded-lg border border-border flex-shrink-0 p-1 sm:p-2"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-16 h-16 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-lg bg-secondary/10 border border-border flex items-center justify-center text-secondary flex-shrink-0">
                            <Plus className="w-6 h-6 sm:w-7 sm:h-7" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-primary text-sm sm:text-base leading-tight break-words">
                            {addonGroup.name}
                          </h3>
                          {addonGroup.hidden && (
                            <div className="mt-1">
                              <HiddenProductBadge />
                            </div>
                          )}
                          <p className="text-xs text-secondary mt-1">
                            {t('newBooking.variantsCount', { count: Object.keys(addonGroup.sizes).length })}
                          </p>
                        </div>
                      </div>

                      {/* Size Buttons */}
                      <div className="flex flex-wrap gap-sm">
                        {Object.entries(addonGroup.sizes).map(([size, items]) => {
                          const firstItem = items[0];
                          const inventoryType = firstItem.inventory_type;

                          // Cart-aware remaining (addons are never bundles).
                          const isUnlimited = inventoryType === 'unlimited';
                          const availableCount = isUnlimited
                            ? Infinity
                            : inventoryType === 'pool'
                              ? cartAvailability.remainingPool(firstItem.product_type_id)
                              : cartAvailability.remainingIndividualItems(firstItem.product_type_id, firstItem.size).length;

                          const soldOut = !isUnlimited && availableCount <= 0;
                          const stockLevel = isUnlimited ? 'high' : (availableCount >= 5 ? 'high' : availableCount >= 3 ? 'medium' : 'low');
                          const badgeStyles = {
                            high: 'bg-green-100 hover:bg-green-200 border-green-300 text-green-800',
                            medium: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300 text-yellow-800',
                            low: 'bg-red-100 hover:bg-red-200 border-red-300 text-red-800'
                          };

                          return (
                            <button
                              key={size}
                              type="button"
                              disabled={soldOut}
                              onClick={() => handleAddAddon(items[0])}
                              className={`inline-flex items-center gap-xs px-md py-2 border rounded-full transition-all font-semibold text-sm ${soldOut ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed opacity-60' : `hover:shadow-md active:scale-[0.98] ${badgeStyles[stockLevel]}`}`}
                              data-testid={`new-booking-addon-variant-button-${addonName}-${size}`}
                            >
                              <span className="truncate max-w-[10rem]">{size === 'One Size' ? t('newBooking.oneSize') : size}</span>
                              <span className="font-bold flex-shrink-0">({isUnlimited ? '∞' : availableCount})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
                </div>
              )}

              {/* STEP 5: Delivery Address */}
              {currentStep === 5 && selectedLocation?.deliveries && (
                <div className="space-y-lg">
                  <div className="flex items-center gap-md mb-lg">
                    <Truck className="w-6 h-6 text-blue" />
                    <h2 className="text-2xl font-bold text-primary">{t('newBooking.deliveryAddress')}</h2>
                  </div>

            <div className="space-y-md">
              {/* Address Autocomplete */}
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  {t('newBooking.deliveryAddress')} <span className="text-danger">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none z-10" />
                  <input
                    type="text"
                    value={deliveryAddressInput}
                    onChange={(e) => handleAddressInputChange(e.target.value)}
                    placeholder={t('newBooking.addressPlaceholder')}
                    className="input pl-10 w-full"
                  />
                  {loadingAddress && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue animate-spin" />
                  )}
                </div>

                {/* Autocomplete Suggestions */}
                {addressSuggestions.length > 0 && (
                  <div className="mt-xs border border-border rounded-lg overflow-hidden bg-primary shadow-lg max-h-60 overflow-y-auto">
                    {addressSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleSelectAddress(suggestion)}
                        className="w-full px-md py-sm text-left hover:bg-secondary/10 transition-colors border-b border-border last:border-b-0"
                      >
                        <p className="text-sm font-medium text-primary">
                          {suggestion.structured_formatting?.main_text || suggestion.description}
                        </p>
                        {suggestion.structured_formatting?.secondary_text && (
                          <p className="text-xs text-secondary">
                            {suggestion.structured_formatting.secondary_text}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Validated Address Display */}
                {validatedAddress && (
                  <div className="mt-md p-md bg-success/10 border border-success/20 rounded-lg">
                    <div className="flex items-start gap-md">
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-success mb-xs">
                          {t('newBooking.addressValidated')}
                        </p>
                        <p className="text-sm text-primary">{validatedAddress.formatted_address}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Delivery Notes */}
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  {t('newBooking.deliveryNotesOptional')}
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-5 h-5 text-secondary pointer-events-none" />
                  <textarea
                    value={deliveryNotes}
                    onChange={(e) => setDeliveryNotes(e.target.value)}
                    placeholder={t('newBooking.deliveryNotesPlaceholder')}
                    rows={3}
                    className="input pl-10 w-full resize-none" inputMode="text"
                  />
                </div>
              </div>

              {/* Delivery Price Preview */}
              {validatedAddress && (
                <div className="p-lg bg-blue/5 border-2 border-blue/20 rounded-xl">
                  {loadingDeliveryPrice ? (
                    <div className="flex items-center justify-center py-md">
                      <Loader2 className="w-6 h-6 text-blue animate-spin mr-md" />
                      <span className="text-secondary">{t('newBooking.calculatingDeliveryPrice')}</span>
                    </div>
                  ) : deliveryPrice !== null && deliveryDistance !== null ? (
                    <div className="space-y-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-secondary">{t('newBooking.distance')}</span>
                        <span className="font-medium text-primary">
                          {deliveryDistance.toFixed(1)} km ({(deliveryDistance * 0.621371).toFixed(1)} mi)
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-secondary">{t('newBooking.deliveryFee')}</span>
                        <span className="font-semibold text-success text-lg">
                          {formatCurrency(deliveryPrice, currencyCode)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-md">
                      <AlertTriangle className="w-5 h-5 text-warning" />
                      <p className="text-sm text-secondary">
                        {t('newBooking.unableToCalculateDelivery')}
                      </p>
                    </div>
                  )}
                </div>
              )}
                </div>
                </div>
              )}

              {/* STEP 6: Customer Information */}
              {currentStep === 6 && (
                <div className="space-y-lg">
                  <div className="flex items-center gap-md mb-lg">
                    <User className="w-6 h-6 text-blue" />
                    <h2 className="text-2xl font-bold text-primary">{t('newBooking.customerInformation')}</h2>
                  </div>

                  {/* Search existing customer */}
                  <div ref={customerSearchRef} className="relative">
                    <label className="block text-sm font-medium text-primary mb-xs">
                      {t('newBooking.searchExistingCustomer')}
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                      <input
                        type="text"
                        value={customerSearchQuery}
                        onChange={(e) => {
                          setCustomerSearchQuery(e.target.value);
                          setCustomerAutofilled(false);
                        }}
                        onFocus={() => {
                          if (customerSearchResults.length > 0) setCustomerSearchOpen(true);
                        }}
                        placeholder={t('newBooking.searchCustomerPlaceholder')}
                        className="input pl-10 w-full"
                      />
                      {loadingCustomerSearch && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary animate-spin" />
                      )}
                    </div>

                    {/* Dropdown results */}
                    {customerSearchOpen && (
                      <div className="absolute z-50 mt-1 w-full bg-primary border border-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {customerSearchResults.length === 0 ? (
                          <div className="px-4 py-3 text-sm text-secondary">{t('newBooking.noPreviousCustomers')}</div>
                        ) : (
                          customerSearchResults.map((c, i) => (
                            <button
                              key={`${c.customer_email}-${i}`}
                              type="button"
                              onClick={() => selectCustomer(c)}
                              className="w-full text-left px-4 py-3 hover:bg-secondary/10 border-b border-border last:border-b-0 transition-colors"
                            >
                              <div className="font-medium text-primary">
                                {c.customer_firstname} {c.customer_lastname}
                              </div>
                              <div className="text-sm text-secondary flex items-center gap-md mt-0.5">
                                <span className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" /> {c.customer_email}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {c.customer_phone}
                                </span>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {customerAutofilled && (
                    <div className="flex items-center gap-sm text-sm text-success">
                      <Check className="w-4 h-4" />
                      <span>{t('newBooking.loadedFromPrevious')}</span>
                    </div>
                  )}

            <div className="border-t border-border pt-lg">
              <p className="text-sm text-secondary mb-md">{t('newBooking.customerDetails')}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
                {/* First Name */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('newBooking.firstName')} <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="text"
                      value={customerFirstName}
                      onChange={(e) => setCustomerFirstName(e.target.value)}
                      placeholder="John"
                      className="input pl-10 w-full"
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('newBooking.lastName')} <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="text"
                      value={customerLastName}
                      onChange={(e) => setCustomerLastName(e.target.value)}
                      placeholder="Smith"
                      className="input pl-10 w-full"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('newBooking.email')} <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="john@example.com"
                      className="input pl-10 w-full"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('newBooking.phone')} <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <input
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="input pl-10 w-full"
                    />
                  </div>
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-primary mb-xs">
                    {t('newBooking.language')}
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                    <select
                      value={customerLanguage}
                      onChange={(e) => setCustomerLanguage(e.target.value)}
                      className="input pl-10 w-full"
                    >
                      {(merchant?.supported_languages && merchant.supported_languages.length > 0
                        ? merchant.supported_languages
                        : [merchant?.default_language || 'en']
                      ).map((lang) => (
                        <option key={lang} value={lang}>
                          {getLanguageConfig(lang).label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Marketing Consent */}
            {merchant?.consent_checkbox_label && (
              <div className="border-t border-border pt-lg">
                <label className="flex items-start gap-md cursor-pointer">
                  <input
                    type="checkbox"
                    checked={customerMarketingConsent}
                    onChange={(e) => setCustomerMarketingConsent(e.target.checked)}
                    className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue focus:ring-blue"
                  />
                  <span className="text-sm text-primary">
                    {(merchant.consent_checkbox_label as Record<string, string>)?.[customerLanguage || merchant.default_language || 'en']
                      || (merchant.consent_checkbox_label as Record<string, string>)?.['en']
                      || Object.values(merchant.consent_checkbox_label as Record<string, string>)[0]
                      || 'I agree to receive marketing communications'}
                  </span>
                </label>
              </div>
            )}

            {/* Custom Fields */}
            {resolvedCustomFields.length > 0 && (
              <div className="border-t border-border pt-lg">
                <h3 className="text-md font-semibold text-primary mb-md flex items-center gap-xs">
                  <FileText className="w-5 h-5 text-blue" />
                  {t('newBooking.additionalInformation')}
                </h3>
                <div className="space-y-md">
                  {resolvedCustomFields.map((field) => (
                    <div key={field.key}>
                      {/* Labels may carry the HTML the booking frontend allows (<br>, <b>, links). */}
                      {field.type === 'info' ? (
                        <p
                          className="text-sm text-secondary"
                          dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(field.label) }}
                        />
                      ) : (
                        <label className="block text-sm font-medium text-primary mb-xs">
                          <span dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(field.label) }} />
                          {field.required && <span className="text-danger"> *</span>}
                        </label>
                      )}
                      {field.type === 'text' && (
                        <input
                          type="text"
                          value={customFieldValues[field.key] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                          maxLength={field.maxLength || 200}
                          className="input w-full"
                          placeholder={customFieldPlaceholder(field)}
                        />
                      )}
                      {field.type === 'textarea' && (
                        <textarea
                          value={customFieldValues[field.key] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                          maxLength={field.maxLength || 1000}
                          rows={field.rows || 4}
                          className="input w-full"
                          placeholder={customFieldPlaceholder(field)}
                        />
                      )}
                      {field.type === 'date' && (
                        <input
                          type="date"
                          value={customFieldValues[field.key] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                          className="input w-full"
                        />
                      )}
                      {field.type === 'datetime' && (
                        <input
                          type="datetime-local"
                          value={customFieldValues[field.key] || ''}
                          onChange={(e) => setCustomFieldValues(prev => ({ ...prev, [field.key]: e.target.value }))}
                          className="input w-full"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
                </div>
              )}

              {/* STEP 7: Payment */}
              {currentStep === 7 && (
                <div className="space-y-lg">
                  <div className="flex items-center gap-md mb-lg">
                    <CreditCard className="w-6 h-6 text-blue" />
                    <h2 className="text-2xl font-bold text-primary">{t('newBooking.paymentTitle')}</h2>
                  </div>

                  {/* Mobile: reserve space so the fixed cart bar doesn't hide content */}
                  <div className="h-16 lg:hidden" aria-hidden="true" />

            {/* Booking Summary */}
            <div className="p-lg bg-secondary/5 rounded-xl border border-border">
              <h3 className="font-semibold text-primary mb-md">{t('newBooking.bookingSummary')}</h3>
              <div className="space-y-sm text-sm">
                <div className="flex justify-between">
                  <span className="text-secondary">{t('newBooking.locationLabel')}</span>
                  <span className="font-medium text-primary">{selectedLocation?.location_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary">{t('newBooking.durationLabel')}</span>
                  <span className="font-medium text-primary">
                    {durationDays}d {durationHours}h
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-secondary">{t('newBooking.productsLabel')}</span>
                  <span className="font-medium text-primary">{t('newBooking.itemsLabel', { count: selectedProducts.length })}</span>
                </div>
                {discountValidation?.valid && (
                  <div className="flex justify-between text-green-700">
                    <span className="text-sm">{t('newBooking.discountAppliedLabel', { code: discountCode })}</span>
                    <span className="font-medium">-{formatCurrency(discountValidation.discount, currencyCode)}</span>
                  </div>
                )}
                {(() => {
                  const taxRate = selectedLocation?.tax_rate || 0;
                  const taxIncluded = selectedLocation?.tax_included ?? true;
                  const taxName = selectedLocation?.tax_name || 'VAT';
                  const baseTotal = calculateTotal();
                  const taxAmt = !taxIncluded && taxRate > 0 ? baseTotal * taxRate : 0;
                  return (
                    <>
                      {taxRate > 0 && !taxIncluded && (
                        <div className="flex justify-between text-sm">
                          <span className="text-secondary">{taxName} ({(taxRate * 100).toFixed(0)}%):</span>
                          <span className="font-medium text-primary">{formatCurrency(taxAmt, currencyCode)}</span>
                        </div>
                      )}
                    </>
                  );
                })()}
                <div className="border-t border-border pt-sm mt-sm">
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold text-primary">{t('newBooking.totalAmount')}</span>
                    <span className="font-bold text-success" data-testid="new-booking-payment-step-total">{formatCurrency((() => {
                      const taxRate = selectedLocation?.tax_rate || 0;
                      const taxIncluded = selectedLocation?.tax_included ?? true;
                      const baseTotal = calculateTotal();
                      return taxIncluded ? baseTotal : baseTotal + baseTotal * taxRate;
                    })(), currencyCode)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className="block text-sm font-medium text-primary mb-md">
                {t('newBooking.paymentMethod')} <span className="text-danger">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('paid')}
                  className={`p-lg border-2 rounded-xl transition-all ${
                    effectivePaymentMethod === 'paid'
                      ? 'border-blue bg-blue/5'
                      : 'border-border hover:border-blue/50'
                  }`}
                >
                  <Check className={`w-6 h-6 mb-md ${effectivePaymentMethod === 'paid' ? 'text-blue' : 'text-secondary'}`} />
                  <p className="font-semibold text-primary mb-xs">{t('newBooking.markAsPaid')}</p>
                  <p className="text-xs text-secondary">{t('newBooking.paymentReceived')}</p>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('link')}
                  disabled={isFullyDiscounted}
                  className={`p-lg border-2 rounded-xl transition-all ${
                    effectivePaymentMethod === 'link'
                      ? 'border-blue bg-blue/5'
                      : 'border-border hover:border-blue/50'
                  } ${isFullyDiscounted ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <CreditCard className={`w-6 h-6 mb-md ${effectivePaymentMethod === 'link' ? 'text-blue' : 'text-secondary'}`} />
                  <p className="font-semibold text-primary mb-xs">{t('newBooking.sendPaymentLink')}</p>
                  <p className="text-xs text-secondary">{t('newBooking.emailPaymentRequest')}</p>
                </button>

                <button
                  type="button"
                  onClick={() => setPaymentMethod('later')}
                  disabled={isFullyDiscounted}
                  className={`p-lg border-2 rounded-xl transition-all ${
                    effectivePaymentMethod === 'later'
                      ? 'border-blue bg-blue/5'
                      : 'border-border hover:border-blue/50'
                  } ${isFullyDiscounted ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Clock className={`w-6 h-6 mb-md ${effectivePaymentMethod === 'later' ? 'text-blue' : 'text-secondary'}`} />
                  <p className="font-semibold text-primary mb-xs">{t('newBooking.payLater')}</p>
                  <p className="text-xs text-secondary">{t('newBooking.paymentPending')}</p>
                </button>
              </div>
              {isFullyDiscounted && (
                <p className="text-xs text-secondary mt-sm" data-testid="new-booking-fully-discounted-note">
                  {t('newBooking.fullyDiscountedNote')}
                </p>
              )}
            </div>

            {/* Discount Code */}
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('newBooking.discountCodeLabel')}
              </label>
              <div className="flex gap-sm">
                <div className="relative flex-1">
                  <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary pointer-events-none" />
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyDiscount();
                      }
                    }}
                    placeholder={t('newBooking.discountCodePlaceholder')}
                    className="input pl-10 w-full uppercase"
                    disabled={validatingDiscount}
                  />
                </div>
                {discountValidation?.valid ? (
                  <button
                    type="button"
                    onClick={handleClearDiscount}
                    className="btn btn-ghost"
                  >
                    <X className="w-4 h-4" />
                    {t('newBooking.clearDiscount')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleApplyDiscount}
                    disabled={!discountCode.trim() || validatingDiscount}
                    className="btn btn-primary"
                  >
                    {validatingDiscount ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('newBooking.validating')}
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        {t('newBooking.apply')}
                      </>
                    )}
                  </button>
                )}
              </div>
              {discountValidation && (
                <div className={`mt-sm p-sm rounded-lg flex items-center gap-xs ${
                  discountValidation.valid
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}>
                  {discountValidation.valid ? (
                    <>
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">
                        {t('newBooking.discountAppliedMessage', { amount: formatCurrency(discountValidation.discount, currencyCode) })}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm">
                        {discountValidation.reason && DISCOUNT_REJECTION_REASON_KEYS[discountValidation.reason]
                          ? t(DISCOUNT_REJECTION_REASON_KEYS[discountValidation.reason], { defaultValue: discountValidation.error ?? '' })
                          : discountValidation.error}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Internal Notes */}
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('newBooking.internalNotesLabel')}
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-5 h-5 text-secondary pointer-events-none" />
                <textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder={t('newBooking.internalNotesPlaceholder')}
                  rows={4}
                  className="input pl-10 w-full resize-none" inputMode="text"
                />
              </div>
                </div>
                </div>
              )}
            </div>

            {/* Order Summary - fixed width on right */}
            <div className="lg:w-80 xl:w-96 flex-shrink-0">
              <div className="lg:sticky lg:top-4">
                {renderOrderSummary()}
              </div>
            </div>
          </div>

          {/* Mobile cart feedback bar (visible from step 3 onward) */}
          <div
            className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-white px-md py-sm shadow-lg"
            data-testid="new-booking-mobile-cart-bar"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.25rem)' }}
          >
            <div className="flex items-center justify-between gap-md">
              <div className="text-sm text-primary">
                <span className="font-semibold">
                  {cartCount}
                </span>{' '}
                item{cartCount !== 1 ? 's' : ''} in cart
              </div>
              <button
                type="button"
                className="btn btn-primary"
                disabled={cartCount === 0}
                aria-disabled={cartCount === 0}
                onClick={() => {
                  try {
                    orderSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  } catch {
                    // ignore
                  }
                }}
                data-testid="new-booking-mobile-view-cart-button"
              >
                View cart
              </button>
            </div>
          </div>
          </>

        ) : (
          // Steps 1-2 use full width (no grid)
          <div></div>
        )}
      </div>
    </div>
  );
}
